import { isAccountAppHost, isCertificationAppHost } from "../auth/app-origins";

export const PREFERRED_LANGUAGE_KEY = "lion_preferred_language";
export const PREFERRED_TIMEZONE_KEY = "lion_preferred_timezone";
/** Bump when cache shape/semantics change so clients drop stale module blobs. */
export const MODULE_CACHE_SCHEMA = "v2";
export const MODULE_CACHE_PREFIX = `lion_i18n_module:${MODULE_CACHE_SCHEMA}:`;

export type AppLocalizationContext = "pro" | "certification" | "account";

export function detectAppContext(): AppLocalizationContext {
  const appTarget = import.meta.env.VITE_APP_TARGET as string | undefined;
  if (isAccountAppHost() || appTarget === "account") {
    return "account";
  }
  if (isCertificationAppHost() || appTarget === "certification") {
    return "certification";
  }
  return "pro";
}

export function getStoredLanguage(): string | null {
  try {
    const value = localStorage.getItem(PREFERRED_LANGUAGE_KEY);
    return value && value.trim() ? value.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

export function setStoredLanguage(code: string): void {
  try {
    localStorage.setItem(PREFERRED_LANGUAGE_KEY, code.trim().toLowerCase());
  } catch {
    // ignore quota / private mode
  }
}

export function getStoredTimeZone(): string | null {
  try {
    const value = localStorage.getItem(PREFERRED_TIMEZONE_KEY);
    return value && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
}

export function setStoredTimeZone(tz: string): void {
  try {
    localStorage.setItem(PREFERRED_TIMEZONE_KEY, tz.trim());
  } catch {
    // ignore
  }
}

export interface CachedModule {
  version: number;
  definitions: Record<string, string>;
}

export function readCachedModule(
  lang: string,
  context: string,
): CachedModule | null {
  try {
    const raw = localStorage.getItem(`${MODULE_CACHE_PREFIX}${lang}:${context}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedModule;
    if (
      typeof parsed?.version !== "number" ||
      !parsed.definitions ||
      typeof parsed.definitions !== "object"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedModule(
  lang: string,
  context: string,
  module: CachedModule,
): void {
  try {
    localStorage.setItem(
      `${MODULE_CACHE_PREFIX}${lang}:${context}`,
      JSON.stringify(module),
    );
  } catch {
    // ignore
  }
}

export function removeCachedModule(lang: string, context: string): void {
  try {
    localStorage.removeItem(`${MODULE_CACHE_PREFIX}${lang}:${context}`);
  } catch {
    // ignore
  }
}

/** Drop all module caches for a language (e.g. after delete/disable). */
export function purgeLanguageModuleCaches(lang: string): void {
  const code = lang.trim().toLowerCase();
  if (!code || code === "en") return;
  try {
    const prefix = `${MODULE_CACHE_PREFIX}${code}:`;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Drop pre-schema / corrupted module caches so users are not stuck on stale keys. */
export function purgeLegacyModuleCaches(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      // Legacy unversioned prefix, or any non-current schema prefix.
      if (
        key.startsWith("lion_i18n_module:") &&
        !key.startsWith(MODULE_CACHE_PREFIX)
      ) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Minimal English chrome so first paint never blocks on network. */
export const BOOTSTRAP_EN: Record<string, string> = {
  "shared.userMenu.profile": "Profile",
  "shared.userMenu.changeLanguage": "Change Language",
  "shared.userMenu.logOut": "Log out",
  "shared.languagePicker.title": "Choose language",
  "shared.languagePicker.close": "Close",
  "shared.common.cancel": "Cancel",
  "shared.common.save": "Save",
  "shared.common.create": "Create",
  "shared.common.delete": "Delete",
  "shared.common.edit": "Edit",
  "shared.common.loading": "Loading…",
  "shared.common.saving": "Saving…",
  "shared.common.close": "Close",
  "shared.map.viewDetails": "View details",
  "account.auth.language": "Language",
  "pro.locale.title": "Language & region",
  "pro.locale.subtitleSelf": "Language and timezone",
  "pro.locale.subtitleAdmin": "Language and timezone",
  "pro.profile.accountDetailsSubtitle": "Name and photo",
  "pro.profile.contactMethodsSubtitle": "Email and phone",
  "pro.profile.securitySubtitle": "Password and account",
  "pro.profile.securityGoogleSubtitle": "Managed via Google",
  "pro.locale.language": "Language",
  "pro.locale.timezone": "Timezone",
  "pro.locale.save": "Save preferences",
  "pro.locale.unsavedChanges": "Unsaved changes",
  "pro.locale.updated": "Language and region preferences updated.",
  "pro.locale.failedUpdate": "Failed to update language and region preferences",

  // Common across account flows
  "account.common.sessionExpired": "Session expired. Please sign in again.",
  "account.common.emailLabel": "Email address",
  "account.common.emailPlaceholder": "you@company.com",
  "account.common.passwordLabel": "Password",
  "account.common.sending": "Sending…",

  // Login page
  "account.login.title": "Sign in to Lion Pro",
  "account.login.subtitle": "Welcome back — enter your details to continue.",
  "account.login.certTitle": "Sign in to get certified",
  "account.login.certSubtitle":
    "Enter your Lion Pro account details to access your technician training.",
  "account.login.certProgressBannerTitle": "Had a certification account before?",
  "account.login.certProgressBannerBody":
    "If you had progress in the previous Certification Portal, sign in or register with the same email address and your progress will carry over.",
  "account.login.continueWithGoogle": "Continue with Google",
  "account.login.signIn": "Sign in",
  "account.login.forgotPassword": "Forgot your password?",
  "account.login.noAccount": "Don't have an account?",
  "account.login.createAccount": "Create one",
  "account.login.incorrectCredentials":
    "Incorrect email or password. Please try again.",
  "account.login.googleSignInError": "Could not start Google sign-in",

  // Smart Link onboarding (login found an unlinked Lion Smart claim)
  "account.smartLink.onboarding.title": "Link your Lion Smart account",
  "account.smartLink.onboarding.subtitle":
    "We found an existing Lion Smart account for this email. Confirm to link it to your new Lion Energy account.",
  "account.smartLink.onboarding.fieldName": "Name",
  "account.smartLink.onboarding.fieldEmail": "Email",
  "account.smartLink.onboarding.fieldPhone": "Phone",
  "account.smartLink.onboarding.confirm": "Link my account",
  "account.smartLink.onboarding.error":
    "Failed to link your Lion Smart account. Please try again.",

  // Smart Link — registration found an unlinked Lion Smart claim (no ticket
  // yet; the user must log in with their Smart password, not choose a new one)
  "account.smartLink.accountExists.title": "A Lion Smart account already exists",
  "account.smartLink.accountExists.body":
    "This email is already registered with Lion Smart. Please sign in with your Lion Smart password to link your account.",
  "account.smartLink.accountExists.goToSignIn": "Go to sign in",

  // Register page
  "account.register.title": "Create your account",
  "account.register.subtitle": "Takes about 2 minutes.",
  "account.register.firstName": "First name",
  "account.register.firstNamePlaceholder": "Jane",
  "account.register.lastName": "Last name",
  "account.register.lastNamePlaceholder": "Smith",
  "account.register.workEmail": "Work email",
  "account.register.workEmailPlaceholder": "jane@acme.com",
  "account.register.emailHintInvite":
    "Prefilled from your invitation. You may use a different email, but you'll need to verify it before joining the organization.",
  "account.register.emailHintDefault":
    "We'll check if your company already has a Lion Pro account.",
  "account.register.passwordPlaceholder": "Create a password",
  "account.register.confirmPasswordLabel": "Confirm password",
  "account.register.confirmPasswordPlaceholder": "Repeat password",
  "account.register.termsAgreePrefix": "I agree to the",
  "account.register.termsOfService": "Terms of Service",
  "account.register.termsAnd": "and",
  "account.register.privacyPolicy": "Privacy Policy",
  "account.register.continue": "Continue",
  "account.register.alreadyHaveAccount": "Already have an account?",
  "account.register.signIn": "Sign in",
  "account.register.pwCheck8Chars": "At least 8 characters",
  "account.register.pwCheckUppercase": "Uppercase letter",
  "account.register.pwCheckNumber": "Number",
  "account.register.pwCheckSpecial": "Special character",
  "account.register.accountCreated": "Account created",
  "account.register.verifyEmailSentPrefix":
    "We sent a verification link and 6-digit code to",
  "account.register.verifyEmailSentSuffix":
    ". You can verify now or continue setup.",
  "account.register.verifyCodeExpiry":
    "The verification link and code expire in 24 hours.",
  "account.register.resendEmail": "Resend email",
  "account.register.skipForNow": "Skip for now",
  "account.register.errorTermsRequired":
    "Please accept the Terms of Service and Privacy Policy.",
  "account.register.errorPasswordsMismatch": "Passwords do not match.",
  "account.register.errorWeakPassword": "Please choose a stronger password.",
  "account.register.errorRegistrationFailed": "Registration failed",
  "account.register.errorResendFailed": "Failed to resend verification email",

  // Forgot password page
  "account.forgotPassword.title": "Reset your password",
  "account.forgotPassword.subtitle":
    "Enter your email and we'll send you a reset link.",
  "account.forgotPassword.emailLabel": "Email address",
  "account.forgotPassword.emailPlaceholder": "you@company.com",
  "account.forgotPassword.sendResetLink": "Send reset link",
  "account.forgotPassword.backToSignIn": "Back to sign in",
  "account.forgotPassword.errorGeneric": "Something went wrong. Please try again.",
  "account.forgotPassword.checkInboxTitle": "Check your inbox",
  "account.forgotPassword.checkInboxBodyPrefix": "If an account exists for",
  "account.forgotPassword.checkInboxBodySuffix":
    ", you'll receive a reset link shortly. The link expires in 1 hour.",
  "account.forgotPassword.googleCalloutText":
    "If you signed up with Google, you'll need to reset your password through Google instead.",
  "account.forgotPassword.resetViaGoogle": "Reset via Google",
  "account.forgotPassword.resendEmail": "Resend email",
  "account.forgotPassword.useDifferentEmail": "Use a different email",

  // Reset password page
  "account.resetPassword.title": "Choose a new password",
  "account.resetPassword.subtitle": "Make it strong.",
  "account.resetPassword.newPasswordLabel": "New password",
  "account.resetPassword.newPasswordPlaceholder": "At least 8 characters",
  "account.resetPassword.confirmPasswordLabel": "Confirm new password",
  "account.resetPassword.confirmPasswordPlaceholder": "Re-enter your password",
  "account.resetPassword.passwordsMismatch": "Passwords do not match",
  "account.resetPassword.setNewPassword": "Set new password",
  "account.resetPassword.errorFailed":
    "Failed to reset password. The link may have expired.",
  "account.resetPassword.invalidLinkTitle": "Invalid reset link",
  "account.resetPassword.invalidLinkBody":
    "This password reset link is invalid or has expired.",
  "account.resetPassword.requestNewLink": "Request a new link",
  "account.resetPassword.successTitle": "Your password has been set!",
  "account.resetPassword.successBody":
    "You're all set. Use this new password next time you sign in.",
  "account.resetPassword.continue": "Continue",

  // Google OAuth callback
  "account.googleCallback.missingCode": "Missing authorization code from Google.",
  "account.googleCallback.failed": "Google sign-in failed",
  "account.googleCallback.completing": "Completing sign-in…",

  // Accept invite page
  "account.acceptInvite.title": "Organization invitation",
  "account.acceptInvite.loading": "Loading invitation…",
  "account.acceptInvite.invitedPrefix": "You have been invited to join",
  "account.acceptInvite.invitedAsPrefix": "as",
  "account.acceptInvite.emailMismatchPrefix": "You are signed in as",
  "account.acceptInvite.emailMismatchMiddle": ". Sign in as",
  "account.acceptInvite.emailMismatchSuffix": "to accept this invitation.",
  "account.acceptInvite.createAccount": "Create account",
  "account.acceptInvite.logIn": "Log in",
  "account.acceptInvite.redirecting": "Redirecting…",
  "account.acceptInvite.confirmingEmail": "Confirming your email…",
  "account.acceptInvite.returnToLogin": "Return to login",
  "account.acceptInvite.errorInvalid": "This invitation link is invalid.",
  "account.acceptInvite.errorExpired": "This invitation has expired.",
  "account.acceptInvite.errorUsed":
    "This invitation link is invalid or has already been used.",
  "account.acceptInvite.errorLoad": "Could not load invitation",

  // Email code entry component
  "account.emailCode.haveCode": "I have my email code",
  "account.emailCode.enterCode": "Enter email code",
  "account.emailCode.verify": "Verify",

  // OTP digit inputs
  "account.verification.codeDigitAriaLabel": "Code digit {digit}",

  // User setup page
  "account.setup.codeExpiredError":
    "Your verification code has expired. Request a new code to continue.",
  "account.setup.errorCompleteFailed": "Failed to complete setup",

  // User setup — email step
  "account.setup.email.verifiedTitle": "Email verified",
  "account.setup.email.verifiedBody": "Your email has been successfully verified.",
  "account.setup.email.title": "Verify your email",
  "account.setup.email.verifying": "Verifying your email…",
  "account.setup.email.sentCodePrefix": "We sent a 6-digit code to",
  "account.setup.email.codeExpired": "Verification code expired",
  "account.setup.email.codeExpiresAt": "Code expires {time}",
  "account.setup.email.codeExpiresIn24h": "Code expires in 24 hours",
  "account.setup.email.sendNewEmail": "Send new email",
  "account.setup.email.resendEmail": "Resend email",

  // User setup — phone step
  "account.setup.phone.verifiedTitle": "Phone verified",
  "account.setup.phone.verifiedBody":
    "Your phone number has been successfully verified.",
  "account.setup.phone.removePhoneNumber": "Remove phone number",
  "account.setup.phone.removeModalTitle": "Remove phone number?",
  "account.setup.phone.removeModalBody":
    "This clears your phone from your account and you will need to verify again to add a new number.",
  "account.setup.phone.removePhone": "Remove phone",
  "account.setup.phone.title": "Set up SMS verification",
  "account.setup.phone.subtitle":
    "We'll send a 6-digit code to verify your phone number.",
  "account.setup.phone.countryLabel": "Country",
  "account.setup.phone.countryCodeAriaLabel": "Country code",
  "account.setup.phone.phoneNumberLabel": "Phone number",
  "account.setup.phone.sendCode": "Send verification code",
  "account.setup.phone.verifyTitle": "Verify your phone",
  "account.setup.phone.sentCodePrefix": "We sent a 6-digit code to",
  "account.setup.phone.verifyButton": "Verify",
  "account.setup.phone.useDifferentNumber": "Use a different number",
  "account.setup.phone.codeExpired": "Verification code expired",
  "account.setup.phone.codeExpiresAt": "Code expires {time}",
  "account.setup.phone.codeExpiresIn15m": "Code expires in 15 minutes",
  "account.setup.phone.sendNewCode": "Send new code",
  "account.setup.phone.resendCode": "Resend code",
  "account.setup.phone.errorInvalidPhone": "Enter a valid phone number.",
  "account.setup.phone.errorSendFailed": "Failed to send verification code",
  "account.setup.phone.errorResendFailed": "Failed to resend verification code",
  "account.setup.phone.errorCodeRequired":
    "Enter the 6-digit code from your text message.",
  "account.setup.phone.errorVerifyFailed": "Phone verification failed",
  "account.setup.phone.errorRemoveFailed": "Failed to remove phone number",

  // User setup — organization step
  "account.setup.org.completeTitleJoined": "Organization joined",
  "account.setup.org.completeTitlePending": "Membership requested",
  "account.setup.org.yourOrganizations": "Your organizations",
  "account.setup.org.leaveOrg": "Leave organization",
  "account.setup.org.leaveOrgModalTitle": "Leave organization?",
  "account.setup.org.leaveOrgConfirmPrefix":
    "If you confirm, you will no longer be a member of",
  "account.setup.org.leaveOrgConfirmButton": "Leave organization",
  "account.setup.org.title": "Your organization",
  "account.setup.org.subtitle":
    "Search for your organization to join, or create a new one.",
  "account.setup.org.createNew": "Create new organization",
  "account.setup.org.errorLeaveFailed": "Failed to leave organization",

  // User setup navigation tabs
  "account.setup.nav.heading": "Account setup",
  "account.setup.nav.ariaLabel": "Setup tasks",
  "account.setup.nav.taskEmail": "Email",
  "account.setup.nav.taskPhone": "Phone",
  "account.setup.nav.taskOrganization": "Organization",
  "account.setup.nav.statusComplete": "Complete",
  "account.setup.nav.statusPending": "Pending",
  "account.setup.nav.progressAriaLabel": "User setup progress",

  // User setup footer
  "account.setup.footer.dashboard": "Dashboard",
  "account.setup.footer.trainings": "Trainings",
  "account.setup.footer.done": "Done",
  "account.setup.footer.skipForNow": "Skip for now",
  "account.setup.footer.goToDashboard": "Go to dashboard",
  "account.setup.footer.goToTrainings": "Go to trainings",

  // Disabled account status panel
  "account.disabledPanel.disabledLabel": "Disabled",
  "account.disabledPanel.disabledByLabel": "Disabled by",
  "account.disabledPanel.restoreUser": "Restore user account",
  "account.disabledPanel.restoreOrg": "Restore organization",
  "shared.listView.log.viewMeta": "View event meta",
  "shared.listView.log.metaModalTitle": "Event meta",
  "shared.listView.log.copyJson": "Copy JSON",
  "shared.listView.log.copied": "Copied!",
  "shared.listView.log.source.userService": "User service",
  "shared.listView.log.source.productService": "Product service",
  "shared.listView.entityFilter.searchPlaceholder": "Search…",
  "shared.listView.entityFilter.empty": "No matches",
  "shared.listView.entityFilter.loadMore": "Load more",
  "shared.listView.entityFilter.clear": "Clear",
  "shared.listView.entityFilter.done": "Done",
  "shared.listView.entityFilter.remove": "Remove",
  "shared.listView.feed.showing": "Showing {count} events",
  "shared.listView.feed.loadMore": "Load more",
  "shared.listView.feed.loading": "Loading…",
  "shared.listView.feed.end": "You've reached the end",
  "shared.listView.fullscreen": "Fullscreen",
  "shared.listView.enterFullscreen": "Enter fullscreen",
  "shared.listView.exitFullscreen": "Exit fullscreen",
  "pro.system.localization.title": "Localization",
  "pro.system.localization.languages": "Languages",
  "pro.system.localization.create": "Create language",
  "pro.system.featureFlags.create": "Create feature",
  "pro.system.featureFlags.createFlag": "Create feature",
  "pro.system.localization.emptyTitle": "No languages yet",
  "pro.system.localization.emptySubtitle":
    "Create a language to start managing translations.",
  "pro.system.localization.definitions": "Definitions",
  "pro.system.localization.definitions.scrollToUnsaved": "Scroll to first unsaved change",
  "pro.system.localization.editLanguage": "Edit language",
  "pro.system.localization.fillMissing": "Fill missing translations",
  "pro.system.localization.scanGaps": "String gaps",
  "pro.system.history.title": "History",
  "pro.system.history.tab.userService": "User Service",
  "pro.system.history.tab.productService": "Product Service",
  "pro.system.history.tabsAria": "History service",
  "pro.system.history.search": "Search events…",
  "pro.system.history.degraded":
    "Some history could not be loaded ({sources}). Showing available events.",
  "pro.system.history.signInRequired": "Sign in required to view history.",
  "pro.system.history.error.load": "Failed to load history events",
  "pro.system.history.emptyTitle": "No history events",
  "pro.system.history.emptySubtitle":
    "Events will appear here as they are captured.",
  "pro.system.history.col.time": "Time",
  "pro.system.history.col.type": "Type",
  "pro.system.history.col.service": "Service",
  "pro.system.history.col.domain": "Domain",
  "pro.system.history.col.event": "Event",
  "pro.system.history.col.summary": "Summary",
  "pro.system.history.col.user": "User",
  "pro.system.history.col.organization": "Organization",
  "pro.system.history.col.product": "Product",
  "pro.system.history.type.audit": "Audit",
  "pro.system.history.type.security": "Security",
  "pro.system.history.type.error": "Error",
  "pro.system.history.type.notification": "Notification",
  "pro.system.history.service.userService": "User service",
  "pro.system.history.service.productService": "Product service",
  "pro.system.productAnalytics.title": "Product Analytics",
  "pro.system.productAnalytics.tabsAria": "Product Analytics view",
  "pro.system.productAnalytics.tab.sessions": "Sessions",
  "pro.system.productAnalytics.tab.errors": "Errors",
  "pro.system.productAnalytics.signInRequired": "Sign in required to view Product Analytics.",
  "pro.system.productAnalytics.searchSessions": "Search sessions…",
  "pro.system.productAnalytics.searchErrors": "Search errors…",
  "pro.system.productAnalytics.sessionsEmptyTitle": "No sessions yet",
  "pro.system.productAnalytics.sessionsEmptySubtitle":
    "Recorded sessions will appear here as people use Lion Pro and Lion Smart.",
  "pro.system.productAnalytics.errorsEmptyTitle": "No errors captured",
  "pro.system.productAnalytics.errorsEmptySubtitle":
    "Client-side and network errors will appear here as they're captured.",
  "pro.system.productAnalytics.col.started": "Started",
  "pro.system.productAnalytics.col.duration": "Duration",
  "pro.system.productAnalytics.col.appSource": "App",
  "pro.system.productAnalytics.col.page": "Page",
  "pro.system.productAnalytics.col.errors": "Errors",
  "pro.system.productAnalytics.col.status": "Status",
  "pro.system.productAnalytics.col.size": "Size",
  "pro.system.productAnalytics.col.occurred": "Occurred",
  "pro.system.productAnalytics.col.kind": "Kind",
  "pro.system.productAnalytics.col.message": "Message",
  "pro.system.productAnalytics.col.severity": "Severity",
  "pro.system.productAnalytics.col.httpStatus": "HTTP status",
  "pro.system.productAnalytics.col.user": "User",
  "pro.system.productAnalytics.col.organization": "Organization",
  "pro.system.productAnalytics.hasErrors.yes": "Yes",
  "pro.system.productAnalytics.hasErrors.no": "No",
  "pro.system.productAnalytics.replay.loading": "Loading recording…",
  "pro.system.productAnalytics.replay.loadError": "Failed to load this recording.",
  "pro.system.productAnalytics.replay.empty": "No recording available for this session.",
  "pro.system.productAnalytics.replay.iframeTitle": "Session replay",
  "pro.system.productAnalytics.session.replay": "Replay",
  "pro.system.productAnalytics.session.linkedErrors": "Linked errors",
  "pro.system.productAnalytics.session.context": "Context",
  "pro.system.productAnalytics.session.backToSessions": "Back to sessions",
  "pro.system.productAnalytics.session.notFound": "Session not found.",
  "pro.system.productAnalytics.session.loadError": "Failed to load this session.",
  "pro.system.productAnalytics.session.expired":
    "This recording is older than 30 days and is no longer available.",
  "pro.system.productAnalytics.session.masked": "Inputs are masked in this recording.",
  "pro.system.productAnalytics.session.noLinkedErrors": "No errors occurred during this session.",
  "pro.system.productAnalytics.session.userId": "User ID",
  "pro.system.productAnalytics.session.anonymous": "Anonymous / not signed in",
  "pro.system.productAnalytics.session.userAgent": "User agent",
  "pro.system.productAnalytics.session.appVersion": "App version",
  "pro.system.productAnalytics.session.contextNote":
    "User and organization names aren't resolved yet — shown as raw IDs for now.",
  "pro.system.productAnalytics.appSource.smartWeb": "Lion Smart",
  "pro.system.productAnalytics.appSource.proWeb": "Lion Pro",
  "pro.system.productAnalytics.error.details": "Details",
  "pro.system.productAnalytics.error.stackTrace": "Stack trace",
  "pro.system.productAnalytics.error.backToErrors": "Back to errors",
  "pro.system.productAnalytics.error.notFound": "Error event not found.",
  "pro.system.productAnalytics.error.loadError": "Failed to load this error.",
  "pro.system.productAnalytics.error.viewSession": "View session",
  "pro.system.productAnalytics.error.httpMethod": "HTTP method",
  "pro.system.productAnalytics.error.httpUrl": "Request URL",
  "pro.system.productAnalytics.error.fingerprint": "Fingerprint",
  "pro.system.productAnalytics.error.noSession": "No session was recorded alongside this error.",
  "pro.system.productAnalytics.error.noStack": "No stack trace was captured for this error.",
  "pro.system.productAnalytics.error.sourcemapNote":
    "Sourcemap-aware symbolication isn't wired up yet — this is the raw (possibly minified) stack.",
  "pro.system.certification.claims.action": "Legacy claims",
  "pro.system.certification.claims.title": "Legacy progress claims",
  "pro.system.certification.claims.subtitle":
    "Certification progress imported from the legacy portal, waiting for or already claimed by Lion Pro accounts.",
  "pro.system.certification.claims.search": "Search by email, legacy id, or claimed user…",
  "pro.system.certification.claims.emptyTitle": "No legacy claims",
  "pro.system.certification.claims.emptySubtitle":
    "Run the legacy progress import to populate this list.",
  "pro.system.certification.claims.summary":
    "{pending} pending · {claimed} claimed · {total} total",
  "pro.system.certification.claims.col.modules": "Modules",
  "pro.system.certification.claims.col.imported": "Imported",
  "pro.system.certification.claims.col.claimedAt": "Claimed at",
  "pro.system.certification.claims.col.claimedBy": "Claimed by",
  "pro.system.certification.claims.previewAction": "Preview migration",
  "pro.system.certification.claims.downloadCertificateAction":
    "Download certificate",
  "pro.system.certification.claims.noCompletedModules":
    "This claim has no completed modules to certify.",
  "pro.system.certification.claims.previewTitle": "Legacy Progress Preview",
  "pro.system.certification.claims.previewClaimSection": "Legacy Progress",
  "pro.system.certification.claims.previewResultSection": "Projected Progress",
  "pro.system.certification.claims.previewError": "Failed to preview migration",
  "pro.system.certification.claims.previewNoUser": "no Lion user yet",
  "pro.system.certification.claims.previewUnmapped":
    "{count} unmapped training(s)",
  "pro.system.certification.claims.previewTrainings":
    "{passed}/{total} trainings passed",
  "pro.system.certification.claims.previewSections": "{passed}/{total} sections",
  "pro.system.certification.claims.previewCoursesCount":
    "{count, plural, one {# Course with progress} other {# Courses with progress}}",
  "pro.system.certification.claims.previewNoTrainings": "No trainings recorded",
  "pro.system.certification.claims.previewPassed": "Passed",
  "pro.system.certification.claims.previewFailed": "Failed",
  "pro.system.certification.claims.previewVideoWatched": "Video watched",
  "pro.system.certification.claims.previewVideoNotWatched": "Video not watched",
  "pro.system.certification.claims.previewVideoBadge": "Video",
  "pro.system.certification.claims.previewModuleComplete": "All sections complete",
  "pro.system.certification.claims.previewCertNum": "Cert #{num}",
  "pro.system.certification.claims.previewCompleted": "Completed {date}",
  "pro.system.certification.claims.previewUnmappedModule": "Unmapped module",
  "pro.system.certification.claims.previewCarryoverFullCredit":
    "This course format has changed since the legacy portal. Your legacy completion still carries full credit.",
  "pro.system.certification.claims.previewCarryoverPartialReset":
    "This course format has changed since the legacy portal. Partial progress cannot be carried over and must be retaken from the start.",
  "pro.system.certification.claims.previewEmptyModules":
    "No legacy modules on this claim.",
  "pro.system.certification.claims.previewEmptyProjected":
    "No projected certification progress.",
  "pro.system.certification.claims.previewViewJson": "View raw JSON",
  "pro.system.certification.claims.previewClaimJsonTitle":
    "Legacy Progress JSON",
  "pro.system.certification.claims.previewResultJsonTitle":
    "Projected Progress JSON",
  "pro.orgs.invite.claimAvailable": "Progress claim available",
  "pro.orgs.invite.claimUnavailable": "No progress claim available",
  "pro.orgs.invite.previewProgress": "Preview progress",
  "pro.orgs.invite.claimTooltip":
    "This user has saved progress in the legacy certification portal. This progress will carry over when they register for a new Lion Energy Account",
  "pro.system.certification.error.loadClaims": "Failed to load legacy progress claims",
  "certification.shell.brand": "Lion Energy Certification",
  "certification.home.heroTitle": "Become a Certified Technician",
  "certification.home.heroSubtitle":
    "Certification is required to install or service Lion Energy systems. Get started with the courses below!",
  "certification.home.eyebrow": "Certification",
  "certification.home.requiredCourses": "Required Courses",
  "certification.home.showInactive": "Show inactive",
  "certification.home.inactive": "Inactive",
  "certification.home.noCourses":
    "No certification courses have been published yet.",
  "certification.home.viewCourse": "View Course",
  "certification.home.reviewCourse": "Review Course",
  "certification.home.progressBadge": "{passed}/{total} · {percent}%",
  "certification.home.downloadAppTitle": "Download Technician App",
  "certification.home.googlePlay": "Get it on Google Play",
  "certification.home.appStore": "Download on the App Store",
  "certification.home.excellenceTitle": "Technicians Strive For Excellence",
  "certification.home.excellenceBody":
    "Welcome to the team! We are dedicated to delivering high-quality products and outstanding technical services to our customers, and we're proud of the standards we uphold. As a representative of our team, your work plays an important role in showcasing the excellence we all strive for every day.",
  "certification.home.technicianPhotoAlt": "Technician at work",
  "certification.home.loadCoursesError": "Failed to load courses",
  "certification.course.notFound": "Course not found.",
  "certification.course.back": "Back",
  "certification.course.beginCourse": "Begin Course",
  "certification.course.sections": "Course Sections",
  "certification.course.estTime": "Est. Course Time",
  "certification.course.hoursUnit": "hr",
  "certification.course.minutesUnit": "min",
  "certification.course.minutesBadge": "{count} Min",
  "certification.course.remainingMinutes": "{count} min remaining",
  "certification.course.remainingHours": "{count} hr remaining",
  "certification.course.remainingHoursMinutes": "{hours} hr {minutes} min remaining",
  "certification.course.remainingNone": "Course complete",
  "certification.course.continueCourse": "Continue Course",
  "certification.course.continueCourseNamed": "Next: {title}",
  "certification.course.reviewLesson": "Review Lesson",
  "certification.course.retakeQuiz": "Retake Quiz",
  "certification.course.completed": "Completed",
  "certification.course.questionsCount": "{count, plural, one {# question} other {# questions}}",
  "certification.course.scoreSummary": "{correct}/{total} · {score}%",
  "certification.course.attemptCount": "{count, plural, one {# attempt} other {# attempts}}",
  "certification.course.videoWatchedPercent": "{percent}% of video watched",
  "certification.course.lastAttempt": "Last attempt {when}",
  "certification.course.youtubeVideo": "YouTube video",
  "certification.course.noTrainings":
    "No training steps have been published for this course yet.",
  "certification.course.tenMin": "10 Min",
  "certification.course.watchVideo": "Watch Video",
  "certification.course.takeQuiz": "Take Quiz",
  "certification.course.startLesson": "Start Lesson",
  "certification.lesson.notFound": "Lesson not found.",
  "certification.lesson.backToCourse": "← Back to course",
  "certification.lesson.back": "← Back",
  "certification.lesson.noVideo":
    "No video has been uploaded for this lesson yet.",
  "certification.lesson.transcript": "Transcript",
  "certification.lesson.showTranscript": "Show transcript",
  "certification.lesson.hideTranscript": "Hide transcript",
  "certification.lesson.skip": "Skip",
  "certification.lesson.startQuiz": "Start Quiz",
  "certification.lesson.startQuizWatchRequired":
    "Watch {percent}% of the video to start the quiz.",
  "certification.lesson.startQuizTrackingUnavailable":
    "Video progress could not be measured. Replay the lesson after the player loads, or contact support.",
  "certification.lesson.youtubeTrainingVideo": "YouTube training video",
  "certification.quiz.notFound": "Quiz not found",
  "certification.quiz.checkFailed": "Failed to check answer",
  "certification.quiz.submitFailed": "Failed to submit quiz",
  "certification.quiz.backToCourse": "← Back to course",
  "certification.quiz.backToCourseShort": "Back to course",
  "certification.quiz.noQuestions":
    "No quiz questions have been published for this lesson yet.",
  "certification.quiz.passed": "You passed!",
  "certification.quiz.failed": "Not quite — retake needed",
  "certification.quiz.scoreSummary": "{correct}/{total} correct ({score}%)",
  "certification.quiz.questionProgress": "Question {current}/{total}",
  "certification.quiz.questionIndex": "Question {current}/{total}",
  "certification.quiz.incorrectFeedback":
    "That's not correct. You can't change your answer, but you can keep going.",
  "certification.quiz.correctFeedback": "Correct!",
  "certification.quiz.finish": "Finish Quiz",
  "certification.quiz.next": "Next Question",
  "certification.quiz.lastQuestion": "Last Question",
  "certification.quiz.backToVideo": "Video",
  "certification.quiz.nextSection": "Next Section",
  "certification.quiz.wellDone": "Well done.",
  "certification.quiz.wellDoneNamed": "Well done, {name}.",
  "certification.quiz.wellDoneSubtitle":
    "You are one step closer to successful Sanctuary installs.",
  "certification.quiz.courseCompleted": "Course completed",
  "certification.quiz.completedOn": "Completed on",
  "certification.quiz.duration": "Duration",
  "certification.quiz.certificationNumber": "Certification number",
  "certification.quiz.downloadCertificate": "Certificate",
  "certification.certificate.heading": "Certificate of Completion",
  "certification.certificate.presentedTo": "This certifies that",
  "certification.certificate.completionLine": "has successfully completed",
  "certification.certificate.numberLabel": "Certificate No.",
  "certification.certificate.issuedBy": "Issued by Lion Energy",
  "certification.certificate.download": "Download Certificate",
  "certification.certificate.downloading": "Preparing…",
  "certification.certificate.downloadFailed":
    "Could not generate the certificate. Please try again.",
  "certification.quiz.retake": "Retake Quiz",
  "certification.quiz.reviewHeading": "Your answers",
  "certification.quiz.yourAnswer": "Your answer: {answer}",
  "certification.resources.title": "Resources",
  "certification.resources.empty": "No resources have been published yet.",
  "certification.resources.loadError": "Failed to load resources",
  "certification.resources.typeVideo": "Video",
  "certification.resources.typePdf": "PDF",

  // Unlocalized-string sweep (pro-web i18n:extract)
  "pro.orgs.inviteToEnroll.title": "Invite to Enroll",
  "pro.orgs.inviteToEnroll.role": "Role",
  "pro.orgs.inviteToEnroll.noEnrollRoles": "No enroll-capable roles available",
  "pro.orgs.certifications": "Certifications",
  "pro.orgs.enrollRoles": "Enroll roles:",
  "pro.orgs.noneConfigured": "None configured",
  "pro.orgs.aboutEnrollRoles": "About enroll roles",
  "pro.orgs.accessRequested": "Access requested",
  "pro.orgs.invitePending": "Invite pending",
  "pro.system.e2e.lastRun": "Last run",
  "pro.system.e2e.lastRunDetails": "Last E2E run details",
  "pro.system.e2e.durationBreakdown": "Duration breakdown",
  "pro.system.e2e.selectPlaywrightProjects": "Select Playwright projects",
  "pro.system.e2e.health": "E2E Health",
  "pro.system.insights.area.insights": "Insights",
  "pro.system.insights.area.localization": "Localization",
  "pro.system.insights.areaMonth": "{area} this month ({state})",
  "pro.system.insights.areaOff": "off",
  "pro.system.insights.areaOn": "on",
  "pro.system.insights.budget": "AI spend limits (all features)",
  "pro.system.insights.col.calls": "Model calls",
  "pro.system.insights.col.details": "Analyses",
  "pro.system.insights.col.failed": "Failed",
  "pro.system.insights.col.headlines": "Headlines",
  "pro.system.insights.col.organization": "Organization",
  "pro.system.insights.col.products": "Products",
  "pro.system.insights.col.total": "Total",
  "pro.system.insights.exportCsv": "Export CSV",
  "pro.system.insights.exportFailed": "Couldn't export usage.",
  "pro.system.insights.exporting": "Exporting…",
  "pro.system.insights.killSwitchOff":
    "All AI calls are turned off (ai.calls_enabled). Insights headlines use templates, analysis shows as paused, and localization AI fill is unavailable.",
  "pro.system.insights.limitsHint":
    "These limits cover every AI feature on the platform. Each call reserves its worst-case cost before it is sent and is blocked if a limit would be crossed. Change them in System → Features under ai.* (global budgets and per-area switches, budgets and models).",
  "pro.system.insights.loadFailed": "Couldn't load Insights usage.",
  "pro.system.insights.loading": "Loading…",
  "pro.system.insights.noRedis": "product-service has no Redis connection, so all AI calls are blocked.",
  "pro.system.insights.noUsage": "No AI usage in this period.",
  "pro.system.insights.refresh": "Refresh",
  "pro.system.insights.showCurrent": "This month",
  "pro.system.insights.showPrevious": "Last month",
  "pro.system.insights.subtitle": "AI usage and spend limits for Product Insights",
  "pro.system.insights.metrics.allOrganizations": "All organizations",
  "pro.system.insights.metrics.analysisShown": "Opened with an analysis",
  "pro.system.insights.metrics.analysisShownSub": "{n} opened with no analysis (no guidance or paused)",
  "pro.system.insights.metrics.byCode": "By alert code",
  "pro.system.insights.metrics.byCodeHint": "Codes with low helpful rates, many support requests, or no guidance are the next ones to write or revise.",
  "pro.system.insights.metrics.col.guidance": "Guidance",
  "pro.system.insights.metrics.col.helpful": "Helpful (votes)",
  "pro.system.insights.metrics.col.helpfulVotes": "Helpful",
  "pro.system.insights.metrics.col.insight": "Insight",
  "pro.system.insights.metrics.col.insights": "Insights",
  "pro.system.insights.metrics.col.noAnalysis": "Opened, no analysis",
  "pro.system.insights.metrics.col.notHelpful": "Not helpful",
  "pro.system.insights.metrics.col.openRate": "Open rate",
  "pro.system.insights.metrics.col.support": "Support requests",
  "pro.system.insights.metrics.costPerOpened": "AI cost per opened Insight",
  "pro.system.insights.metrics.costSub": "{total} total · {failed} failed calls",
  "pro.system.insights.metrics.created": "Insights created",
  "pro.system.insights.metrics.createdSub": "{opened} opened · {rate} open rate",
  "pro.system.insights.metrics.days": "{n} days",
  "pro.system.insights.metrics.guidanceNo": "None yet",
  "pro.system.insights.metrics.guidanceYes": "Approved",
  "pro.system.insights.metrics.helpful": "Rated helpful",
  "pro.system.insights.metrics.helpfulSub": "{helpful} helpful · {notHelpful} not helpful",
  "pro.system.insights.metrics.hours": "{n} h",
  "pro.system.insights.metrics.impactLine": "Support requests by impact: {down} not working · {degraded} partly working · {working} questions. Median time to handle: {handleTime}.",
  "pro.system.insights.metrics.loadFailed": "Couldn't load pilot metrics.",
  "pro.system.insights.metrics.minutes": "{n} min",
  "pro.system.insights.metrics.noInsights": "No alert Insights in this period.",
  "pro.system.insights.metrics.notHelpfulHint": "Review these weekly. Revising the guidance creates a new version, and the analysis regenerates the next time someone opens the Insight.",
  "pro.system.insights.metrics.notHelpfulNone": "No Insights were voted not helpful in this period.",
  "pro.system.insights.metrics.notHelpfulTitle": "Voted not helpful",
  "pro.system.insights.metrics.organization": "Organization",
  "pro.system.insights.metrics.support": "Support requests",
  "pro.system.insights.metrics.supportSub": "{rate} of opened Insights · {handled} handled",
  "pro.system.insights.metrics.timeToOpen": "Median time to open",
  "pro.system.insights.metrics.timeToOpenSub": "{expired} dropped off unopened",
  "pro.system.insights.metrics.title": "Pilot metrics",
  "pro.system.insights.guidance.addItem": "Add",
  "pro.system.insights.guidance.approve": "Approve",
  "pro.system.insights.guidance.approved": "Approved. Insights for this alert on this catalog now use this guidance.",
  "pro.system.insights.guidance.approvedBy": "Approved by",
  "pro.system.insights.guidance.awaitingOther": "You wrote this version, so someone else needs to approve it.",
  "pro.system.insights.guidance.back": "All guidance",
  "pro.system.insights.guidance.catalogDescription": "Catalog description",
  "pro.system.insights.guidance.changesRequested": "{name} requested changes: {note}",
  "pro.system.insights.guidance.changesSent": "Sent back to draft with your note.",
  "pro.system.insights.guidance.col.alert": "Alert",
  "pro.system.insights.guidance.col.catalog": "Catalog",
  "pro.system.insights.guidance.col.code": "Code",
  "pro.system.insights.guidance.col.fired": "Fired (90 days)",
  "pro.system.insights.guidance.col.status": "Guidance",
  "pro.system.insights.guidance.col.type": "Type",
  "pro.system.insights.guidance.col.updated": "Last edited",
  "pro.system.insights.guidance.conflict": "Someone else changed this guidance since you opened it. Reload the page to see their version before saving.",
  "pro.system.insights.guidance.contactLionIf": "Contact Lion if",
  "pro.system.insights.guidance.contactLionIfHint": "Conditions that mean the customer should stop and call Lion. The analysis picks the ones that fit.",
  "pro.system.insights.guidance.copied": "Copied. Review it for this product line before saving.",
  "pro.system.insights.guidance.copiedDropped": "Copied. These evidence keys don't exist in this catalog and were left out: {keys}",
  "pro.system.insights.guidance.copyApply": "Copy into form",
  "pro.system.insights.guidance.copyChoose": "Choose a catalog…",
  "pro.system.insights.guidance.copyFrom": "Start from another catalog's guidance for this code",
  "pro.system.insights.guidance.editingApproved": "This guidance is live. Saving a change starts a new version that goes through review; Insights keep using this approved version until the new one is approved.",
  "pro.system.insights.guidance.liveVersion": "Live: v{version}",
  "pro.system.insights.guidance.pendingOverLive": "Customers still see approved version v{live}. This version (v{version}) replaces it once approved.",
  "pro.system.insights.guidance.emptySubtitle": "Try a different filter or search.",
  "pro.system.insights.guidance.emptyTitle": "No alerts match",
  "pro.system.insights.guidance.evidenceHint": "Telemetry the analysis should look at around each occurrence. Pick the few that actually tell causes apart.",
  "pro.system.insights.guidance.faultRule": "This is a fault code. Fault guidance should be approved by a hardware or firmware engineer.",
  "pro.system.insights.guidance.intro": "Insights only explain alerts that have approved guidance. Start with the codes that fire most often. Each entry needs a second person to approve it.",
  "pro.system.insights.guidance.itemAria": "Item {index}",
  "pro.system.insights.guidance.keyFilter": "Filter telemetry keys",
  "pro.system.insights.guidance.keysSelected": "{count, plural, =0 {No keys selected} one {# key selected} other {# keys selected}}",
  "pro.system.insights.guidance.likelyCauses": "Likely causes",
  "pro.system.insights.guidance.likelyCausesHint": "Most likely first. The analysis says which one the evidence supports.",
  "pro.system.insights.guidance.loadFailed": "Couldn't load guidance.",
  "pro.system.insights.guidance.moveDown": "Move down",
  "pro.system.insights.guidance.moveUp": "Move up",
  "pro.system.insights.guidance.noKeys": "This catalog has no numeric telemetry keys, so the analysis will use alert history only.",
  "pro.system.insights.guidance.previewLabel": "Headline when no AI call is made:",
  "pro.system.insights.guidance.remove": "Remove",
  "pro.system.insights.guidance.requestChanges": "Request changes",
  "pro.system.insights.guidance.reviewDirty": "You've edited this entry. Save it (which makes you the author) or reload to review the submitted version.",
  "pro.system.insights.guidance.reviewNote": "Review note",
  "pro.system.insights.guidance.reviewNotePlaceholder": "Required when requesting changes: what needs to change?",
  "pro.system.insights.guidance.saveDraft": "Save draft",
  "pro.system.insights.guidance.saveFailed": "Couldn't save guidance.",
  "pro.system.insights.guidance.savedDraft": "Draft saved.",
  "pro.system.insights.guidance.search": "Search by code or alert name",
  "pro.system.insights.guidance.section.alert": "Alert",
  "pro.system.insights.guidance.section.content": "Guidance",
  "pro.system.insights.guidance.section.evidence": "Evidence",
  "pro.system.insights.guidance.section.review": "Save and review",
  "pro.system.insights.guidance.status.approved": "Approved",
  "pro.system.insights.guidance.status.draft": "Draft",
  "pro.system.insights.guidance.status.in_review": "In review",
  "pro.system.insights.guidance.status.none": "None",
  "pro.system.insights.guidance.steps": "What the customer can do",
  "pro.system.insights.guidance.stepsHint": "Only steps a customer can safely do without tools or opening the equipment. Customers see this exact wording.",
  "pro.system.insights.guidance.submit": "Submit for review",
  "pro.system.insights.guidance.submitted": "Submitted for review. Someone else needs to approve it.",
  "pro.system.insights.guidance.summary": "What this alert means",
  "pro.system.insights.guidance.summaryHint": "Plain language, for a fleet manager. The first sentence doubles as the headline when no AI call is made.",
  "pro.system.insights.guidance.summaryRequired": "Add a summary before saving.",
  "pro.system.insights.guidance.title": "Alert guidance",
  "pro.system.insights.guidance.type.alarm": "Alarm",
  "pro.system.insights.guidance.type.fault": "Fault",
  "pro.system.insights.guidance.typeSeverity": "{type} · severity {severity} of 5",
  "pro.system.insights.guidance.unknownKeys": "Not in this catalog, so they'll be ignored: {keys}",
  "pro.system.insights.guidance.updatedBy": "{when} · {name}",
  "pro.system.insights.guidance.version": "Version",
  "pro.system.insights.guidance.versionLine": "v{version}, last edited by {name} on {when}",
  "pro.system.insights.guidanceLink": "Alert guidance",
  "pro.system.insights.support.alertOnly": "Alert {code} (no Insight)",
  "pro.system.insights.support.callback": "Callback",
  "pro.system.insights.support.callbackNone": "None given, use email",
  "pro.system.insights.support.close": "Close",
  "pro.system.insights.support.col.impact": "Impact",
  "pro.system.insights.support.col.product": "Product",
  "pro.system.insights.support.col.received": "Received",
  "pro.system.insights.support.col.reference": "Reference",
  "pro.system.insights.support.col.requester": "Requested by",
  "pro.system.insights.support.col.status": "Status",
  "pro.system.insights.support.col.subject": "About",
  "pro.system.insights.support.detailTitle": "Support request {reference}",
  "pro.system.insights.support.emptySubtitle": "Requests from “Still need help” and “Contact Lion support” appear here.",
  "pro.system.insights.support.emptyTitle": "No support requests",
  "pro.system.insights.support.handledBy": "Marked handled by {name} on {when}.",
  "pro.system.insights.support.handledHeading": "Handled",
  "pro.system.insights.support.handledNote": "Resolution note (optional)",
  "pro.system.insights.support.handledNotePlaceholder": "What was the cause, and what did you tell the customer?",
  "pro.system.insights.support.impact.degraded": "Partly working",
  "pro.system.insights.support.impact.down": "Not working",
  "pro.system.insights.support.impact.working": "Question",
  "pro.system.insights.support.insightHeading": "What the customer saw",
  "pro.system.insights.support.loadFailed": "Couldn't load support requests.",
  "pro.system.insights.support.markHandled": "Mark handled",
  "pro.system.insights.support.noAnalysis": "The full analysis was not generated for this Insight.",
  "pro.system.insights.support.noInsight": "No Insight was attached; the alert had no approved guidance.",
  "pro.system.insights.support.none": "None",
  "pro.system.insights.support.notesHeading": "Customer notes",
  "pro.system.insights.support.reopen": "Reopen",
  "pro.system.insights.support.saveFailed": "Couldn't update the request.",
  "pro.system.insights.support.search": "Search by reference, product, requester or alert code",
  "pro.system.insights.support.status.handled": "Handled",
  "pro.system.insights.support.status.open": "Open",
  "pro.system.insights.support.title": "Support requests",
  "pro.system.insights.support.triedHeading": "Already tried",
  "pro.system.insights.support.usageLink": "Usage & limits",
  "pro.system.insights.supportLink": "Support requests",
  "pro.system.insights.thisMonth": "This month (UTC)",
  "pro.system.insights.title": "Insights",
  "pro.system.insights.today": "Today (UTC)",
  "pro.system.insights.unattributed": "Unattributed (no owning organization)",
  "pro.system.insights.usageByOrg": "Usage by organization",
  "pro.system.e2e.runner": "E2E Runner",
  "pro.system.e2e.report": "E2E Report",
  "pro.system.e2e.noSpecResults": "No per-spec results in the latest summary.",
  "pro.system.e2e.tryClearingFilters": "Try clearing filters or search.",
  "pro.system.modules.uploadTrophy": "Upload trophy",
  "pro.certifications.stats": "Certification stats",
  "certification.quiz.debugPreview": "Preview",
  "certification.quiz.debugActual": "Actual",
  "certification.quiz.debugPassed": "Passed",
  "certification.quiz.debugFailed": "Failed",
  "certification.quiz.debugCourseComplete": "Course complete",
  "shared.certification.progressLabel": "Progress: ",
  "shared.certification.expired": "Certification expired",
  "shared.certification.expiredOn": "Expired on: ",
  "shared.certification.originallyCompleted": "Originally completed: ",
  "shared.certification.certificateNumberLabel": "Certificate No.:",
  "shared.certification.downloadCertificate": "Download certificate",
  "shared.listView.quickFiltersTitle": "Quick Filters",
  "shared.listView.sort": "Sort",
  "shared.listView.quickFiltersLabel": "Quick filters",
  "shared.listView.clearAll": "Clear all",
  "shared.savedView.icon": "Icon",
  "shared.savedView.name": "Name",
  "shared.savedView.color": "Color",
  "shared.savedView.shareWithOrganization": "Share with organization",
  "shared.savedView.removeFromMyList": "Remove from my list",
  "shared.savedView.saveThisView": "Save this view",
  "shared.savedView.personal": "Personal",
  "shared.savedView.shared": "Shared",
  "shared.savedView.savedViews": "Saved views",
  "shared.savedView.settings": "Saved View settings",
  "shared.savedView.clearSavedView": "Clear saved view",
  "shared.filter.setFilters": "Set Filters",
  "shared.filter.setColumns": "Set Columns",

  // Smart Link admin page (was missing defaults entirely — see LocalizationDefinitionsPage fix)
  "pro.system.smartLink.title": "Smart Link",
  "pro.system.smartLink.search": "Search claims by email…",
  "pro.system.smartLink.emptyTitle": "No Smart Link claims yet",
  "pro.system.smartLink.emptySubtitle":
    "Claims will appear here once accounts are imported or synced from Lion Smart.",
  "pro.system.smartLink.signInRequired": "Sign in required to view Smart Link claims.",
  "pro.system.smartLink.error.load": "Failed to load Smart Link claims",
  "pro.system.smartLink.col.email": "Email",
  "pro.system.smartLink.col.name": "Name",
  "pro.system.smartLink.col.linkStatus": "Link Status",
  "pro.system.smartLink.col.linkedProUser": "Linked Lion Energy User",
  "pro.system.smartLink.col.lastSynced": "Last Synced",
  "pro.system.smartLink.col.syncStatus": "Sync Status",
  "pro.system.smartLink.status.unlinked": "Unlinked",
  "pro.system.smartLink.status.disabled": "Disabled",
  "pro.system.smartLink.status.linked": "Linked",
  "pro.system.smartLink.badge": "Smart Link",
  // Shared with the Full Sync modal's environment picker + error states —
  // the single-claim import tool these were originally written for was
  // removed 2026-08-25 (Full Sync supersedes it, including single-entry
  // imports via its checkbox list).
  "pro.system.smartLink.import.environment": "Environment",
  "pro.system.smartLink.import.noEnvironments": "No Lion Smart environments configured",
  "pro.system.smartLink.import.cancel": "Cancel",
  "pro.system.smartLink.import.error.notConfigured":
    "Smart Link integration is not configured on this server.",
  "pro.system.smartLink.import.error.unavailable": "Could not reach Lion Smart. Please try again.",

  // Smart Link "Full Sync" bulk backfill modal
  "pro.system.smartLink.fullSync.button": "Full Sync",
  "pro.system.smartLink.fullSync.title": "Full Sync from Lion Smart",
  "pro.system.smartLink.fullSync.subtitle":
    "Pulls every Lion Smart user Lion Energy doesn't have a claim for yet. Nothing is saved until you review and confirm the list below.",
  "pro.system.smartLink.fullSync.lookup": "Look up claims",
  "pro.system.smartLink.fullSync.back": "Back",
  "pro.system.smartLink.fullSync.nothingToImport": "Everything is already pulled — no new claims found.",
  "pro.system.smartLink.fullSync.selectAll": "Select all",
  "pro.system.smartLink.fullSync.selectedCount": "{selected} of {total} selected",
  "pro.system.smartLink.fullSync.matchBadge": "Matches existing user",
  "pro.system.smartLink.fullSync.matchTooltip":
    "A Lion Energy account with this email already exists. Importing will not link them — they'll be offered linking the next time they sign in with their Lion Smart password.",
  "pro.system.smartLink.fullSync.replaceBadge": "Replaces stale claim",
  "pro.system.smartLink.fullSync.replaceTooltip":
    "An unlinked claim with this email points at a Lion Smart user that no longer exists. Importing replaces it with this user.",
  "pro.system.smartLink.fullSync.conflictBadge": "Needs review",
  "pro.system.smartLink.fullSync.conflictTooltipLinked":
    "A linked claim already uses this email for a different Lion Smart user. Delete that claim from the Smart Link list to pull this one.",
  "pro.system.smartLink.fullSync.conflictTooltip":
    "Another claim already uses this email, and its Lion Smart user still exists. Delete that claim from the Smart Link list to pull this one.",
  "pro.system.smartLink.fullSync.confirmImport": "Import {count} claims",
  "pro.system.smartLink.fullSync.doneMessage": "Imported {created} new claims and replaced {replaced} stale claims ({skipped} skipped).",
  "pro.system.smartLink.delete.action": "Delete claim",
  "pro.system.smartLink.delete.titleOne": "Delete the Smart Link claim for {email}?",
  "pro.system.smartLink.delete.titleMany": "Delete {count} Smart Link claims?",
  "pro.system.smartLink.delete.body":
    "The claim is removed from Lion Energy. Lion Smart users are not touched. Linked Lion Energy users keep their account but are no longer synced with Lion Smart. Run Full Sync to pull the claim again.",
  "pro.system.smartLink.delete.error": "Failed to delete the selected claims.",
  "pro.system.smartLink.fullSync.error.lookup": "Failed to look up claims from Lion Smart. Please try again.",
  "pro.system.smartLink.fullSync.error.commit": "Failed to import the selected claims. Please try again.",

  // Smart Link claim management modal (Settings + Compare Fields sections)
  "pro.system.smartLink.manage.title": "Manage Smart Link",
  "pro.system.smartLink.manage.settingsSection": "Settings",
  // Deliberately "Sync enabled", not "Link enabled" — this toggle controls
  // whether syncing is *allowed*, independent of whether a Pro user is
  // actually paired with this claim yet (see linkedTo/notYetLinked below).
  // A brand-new claim from Full Sync starts sync-enabled but unpaired.
  "pro.system.smartLink.manage.syncEnabled": "Sync enabled",
  "pro.system.smartLink.manage.linkedTo": "Linked to {email}",
  "pro.system.smartLink.manage.notYetLinked":
    "Not yet linked to a Lion Energy account — this happens automatically the first time this person signs in with their Lion Smart password.",
  "pro.system.smartLink.manage.compareDisabled":
    "Sync is disabled for this claim — enable it to compare fields between Lion Energy and Lion Smart.",
  "pro.system.smartLink.manage.toggleError": "Failed to update sync settings. Please try again.",

  // Smart Link claim compare modal
  "pro.system.smartLink.compare.title": "Compare Fields",
  "pro.system.smartLink.compare.loading": "Loading comparison…",
  "pro.system.smartLink.compare.error.load": "Failed to load comparison",
  "pro.system.smartLink.compare.notLinked":
    "This claim isn't linked to a Lion Energy user yet — showing Lion Smart values only.",
  "pro.system.smartLink.compare.smartUnavailable": "Could not fetch live data from Lion Smart",
  "pro.system.smartLink.compare.col.field": "Field",
  "pro.system.smartLink.compare.col.lionEnergy": "Lion Energy",
  "pro.system.smartLink.compare.col.lionSmart": "Lion Smart",
  "pro.system.smartLink.compare.yes": "Yes",
  "pro.system.smartLink.compare.no": "No",
  "pro.system.smartLink.compare.section.profile": "Profile",
  "pro.system.smartLink.compare.section.roles": "Roles",
  "pro.system.smartLink.compare.section.permissions": "Permissions",
  "pro.system.smartLink.compare.matches": "Matches",
  "pro.system.smartLink.compare.mismatch": "Mismatch",
  "pro.system.smartLink.compare.status.synced": "Synced — every value matches between Lion Energy and Lion Smart.",
  "pro.system.smartLink.compare.status.outOfSync": "Out of sync — some values differ between Lion Energy and Lion Smart.",
  "pro.system.smartLink.compare.syncFromPro": "Sync from Pro →",
  "pro.system.smartLink.compare.syncFromSmart": "← Sync from Smart",
  "pro.system.smartLink.compare.syncComingSoon": "Coming soon",
  "pro.system.smartLink.compare.syncFromProError": "Failed to sync from Pro. Please try again.",
  "pro.system.smartLink.compare.syncFromSmartError": "Failed to sync from Smart. Please try again.",
  "pro.system.smartLink.compare.field.email": "Email",
  "pro.system.smartLink.compare.field.firstName": "First Name",
  "pro.system.smartLink.compare.field.lastName": "Last Name",
  "pro.system.smartLink.compare.field.phone": "Phone",
  "pro.system.smartLink.compare.field.phoneVerified": "Phone Verified",
  "pro.system.smartLink.compare.field.emailVerified": "Email Verified",
  "pro.system.smartLink.compare.field.globalRoles": "Global Roles",
  "pro.system.smartLink.compare.field.globalPermissions": "Global Permissions",

  // Smart Link tabs, health and products
  "pro.system.smartLink.tabs.label": "Smart Link entities",
  "pro.system.smartLink.tabs.users": "Users",
  "pro.system.smartLink.tabs.products": "Products",
  "pro.system.smartLink.health.title.users": "User sync health",
  "pro.system.smartLink.health.title.products": "Product sync health",
  "pro.system.smartLink.health.error.load": "Failed to load Smart Link health.",
  "pro.system.smartLink.health.error.run": "Failed to start the reconcile. Please try again.",
  "pro.system.smartLink.health.alreadyRunning": "A reconcile is already running.",
  "pro.system.smartLink.health.reconciling": "Reconciling with Lion Smart…",
  "pro.system.smartLink.health.lastReconciled": "Last reconciled {when}",
  "pro.system.smartLink.health.neverReconciled": "Not reconciled yet",
  "pro.system.smartLink.health.runNow": "Run now",
  "pro.system.smartLink.health.unsupported": "Not counted: Smart categories with no Pro product line — {categories}.",
  "pro.system.smartLink.products.fullSync.unsupported": "Hidden because Pro has no product line for their Smart category: {categories}.",
  "pro.system.smartLink.health.notConfigured": "Lion Smart isn't configured for this service yet, so nothing can be synced or reconciled.",
  "pro.system.smartLink.health.lastRunFailed": "The last reconcile failed: {error}",
  "pro.system.smartLink.health.ringLabel": "{pct} in sync",
  "pro.system.smartLink.health.legend.inSync": "In sync ({count})",
  "pro.system.smartLink.health.legend.outOfSync": "Out of sync ({count})",
  "pro.system.smartLink.health.legend.unknown": "Not checked ({count})",
  "pro.system.smartLink.health.legend.disabled": "Sync disabled ({count})",
  "pro.system.smartLink.health.legend.awaitingLink": "Pulled, not paired ({count})",
  "pro.system.smartLink.health.legend.unlinkedInSmart.users": "In Smart, not pulled ({count})",
  "pro.system.smartLink.health.legend.unlinkedInSmart.products": "In Smart, not in Pro ({count})",
  "pro.system.smartLink.health.unlinkedPending": "Smart-only counts appear after the first reconcile.",
  "pro.system.smartLink.health.excluded.users": "Not counted: {proOnly} Pro-only users.",
  "pro.system.smartLink.health.excluded.products": "Not counted: {proOnly} Pro-only and {simulated} simulated products.",
  "pro.system.smartLink.health.openFailures": "{count} unresolved sync failures",
  "pro.system.smartLink.products.error.load": "Failed to load linked products.",
  "pro.system.smartLink.products.search": "Search linked products",
  "pro.system.smartLink.products.emptyTitle": "No linked products yet",
  "pro.system.smartLink.products.emptySubtitle": "Run Full Sync to link products from Lion Smart.",
  "pro.system.smartLink.products.col.product": "Product",
  "pro.system.smartLink.products.col.serial": "Serial",
  "pro.system.smartLink.products.fullSync.title": "Full Sync products from Lion Smart",
  "pro.system.smartLink.products.fullSync.subtitle": "Finds every Lion Smart product that isn't linked to Lion Energy yet. Nothing is saved until you review and confirm the list.",
  "pro.system.smartLink.products.fullSync.organization": "Owning organization for new products",
  "pro.system.smartLink.products.fullSync.noOrganization": "None (assign later)",
  "pro.system.smartLink.products.fullSync.lookup": "Look up products",
  "pro.system.smartLink.products.fullSync.nothingToImport": "Every Lion Smart product is already linked.",
  "pro.system.smartLink.products.fullSync.includeArchived": "Show archived",
  "pro.system.smartLink.products.fullSync.conflict": "Conflicts with a Pro product",
  "pro.system.smartLink.products.fullSync.existing": "Links existing product",
  "pro.system.smartLink.products.fullSync.existingTooltip": "{name} already uses this Lion Smart ID. It will be linked rather than duplicated.",
  "pro.system.smartLink.products.fullSync.pickModel": "Choose a model",
  "pro.system.smartLink.products.fullSync.unmapped.no_category": "No category in Smart",
  "pro.system.smartLink.products.fullSync.unmapped.unknown_category": "Unknown Smart category",
  "pro.system.smartLink.products.fullSync.unmapped.no_active_model": "No matching Pro model",
  "pro.system.smartLink.products.fullSync.archived": "Archived",
  "pro.system.smartLink.products.fullSync.confirmImport": "Link {count} products",
  "pro.system.smartLink.products.fullSync.doneMessage": "Created {created}, linked {linked} existing, skipped {skipped}.",
  "pro.system.smartLink.products.fullSync.skip.archived": "archived in Smart",
  "pro.system.smartLink.products.fullSync.skip.conflict": "conflicts with an existing Pro product",
  "pro.system.smartLink.products.fullSync.skip.needs_model": "needs a product model",
  "pro.system.smartLink.products.fullSync.skip.error": "failed",
  "pro.system.smartLink.products.fullSync.error.lookup": "Failed to look up products from Lion Smart. Please try again.",
  "pro.system.smartLink.products.fullSync.error.commit": "Failed to link the selected products. Please try again.",
  "pro.system.smartLink.products.manage.title": "Smart Link: {name}",
  "pro.system.smartLink.products.field.name": "Name",
  "pro.system.smartLink.products.field.systemName": "System name",
  "pro.system.smartLink.products.field.smartReferenceId": "Smart reference ID",
  "pro.system.smartLink.products.field.serial": "Serial",
  "pro.system.smartLink.products.field.assetTag": "Asset tag",
  "pro.system.smartLink.products.field.macAddress": "MAC address",
  "pro.system.smartLink.products.field.macAddresses": "All MAC addresses",
  "pro.system.smartLink.products.field.isSimulated": "Simulated",
  "pro.system.smartLink.products.field.isIot": "IoT",
  "pro.system.smartLink.products.field.isCommissioned": "Commissioned",
  "pro.system.smartLink.products.field.isConsumptionConfigured": "Consumption configured",
  "pro.system.smartLink.products.field.hideSolar": "Hide solar",
  "pro.system.smartLink.products.field.hideConsumption": "Hide consumption",
  "pro.system.smartLink.products.field.hideGrid": "Hide grid",
  "pro.system.smartLink.products.field.hideBattery": "Hide battery",
  "pro.system.smartLink.products.field.pvCapacityW": "PV capacity (W)",
  "pro.system.smartLink.products.field.gridType": "Grid type",
  "pro.system.smartLink.products.field.installLocation": "Install location",
  "pro.system.smartLink.products.field.latitude": "Latitude",
  "pro.system.smartLink.products.field.longitude": "Longitude",
  "pro.system.smartLink.products.field.address": "Address",
  "pro.system.smartLink.products.field.addressSecondary": "Address line 2",
  "pro.system.smartLink.products.field.city": "City",
  "pro.system.smartLink.products.field.state": "State",
  "pro.system.smartLink.products.field.zip": "ZIP",
  "pro.system.smartLink.products.field.country": "Country",
  "pro.system.smartLink.products.field.timezone": "Timezone",
  "pro.system.smartLink.products.field.firmwareVersions": "Firmware versions",
  "pro.system.smartLink.products.field.smartSystemFirmwareId": "System firmware",
  "pro.system.smartLink.products.field.systemFirmwareVersion": "System firmware version",
  "pro.system.smartLink.products.field.componentConfiguration": "Component configuration",
  "pro.system.smartLink.products.field.peripheralId": "Peripheral ID",
  "pro.system.smartLink.products.field.bluetoothProtocol": "Bluetooth protocol",
  "pro.system.smartLink.products.field.ssid": "Wi-Fi SSID",
  "pro.system.smartLink.products.field.smartCategoryId": "Smart category",
  "pro.system.smartLink.products.field.smartCategoryProductId": "Smart category product",
  "pro.system.smartLink.products.field.productModelId": "Product model",
  "pro.system.smartLink.products.field.components": "Components",
  "pro.system.smartLink.products.field.archived": "Archived",
  "pro.products.smartLinkSync": "Smart Link",
  "pro.products.bluetoothOnly.title": "Coming soon",
  "pro.products.bluetoothOnly.message": "This is a Bluetooth-only device. It doesn't send data to the cloud yet, so this section isn't available.",
  "pro.products.bluetoothOnly.settingsMessage": "Device settings for Bluetooth-only products aren't available in Lion Energy yet.",
  "pro.products.smartCategoryProductId": "Lion Smart category product ID",
  "pro.products.smartLink.managedNotice": "This product is linked to Lion Smart. Lion Smart owns its model, identifiers, capacity and coordinates; name and address changes sync back to Lion Smart.",

  "pro.system.smartApi.title": "Smart API",
  "pro.system.smartApi.subtitle":
    "Send the same request to smart-api directly and to the pro-api legacy layer, then compare the results.",
  "pro.system.smartApi.loading": "Loading…",
  "pro.system.smartApi.loadFailed": "Couldn't load the endpoint catalog.",
  "pro.system.smartApi.target": "smart-api: {smartApiUrl} · pro-api: {legacyBasePath}",
  "pro.system.smartApi.section.request": "Request",
  "pro.system.smartApi.section.results": "Results",
  "pro.system.smartApi.section.shapeDiff": "Shape differences",
  "pro.system.smartApi.endpoint": "Endpoint",
  "pro.system.smartApi.endpointChoose": "Choose an endpoint…",
  "pro.system.smartApi.mode.native": "Native",
  "pro.system.smartApi.mode.passthrough": "Pass-through",
  "pro.system.smartApi.write": "Write",
  "pro.system.smartApi.read": "Read",
  "pro.system.smartApi.passthroughHint":
    "This route is passed through to smart-api, so both sides should match. Differences point at the proxy, not an adapter.",
  "pro.system.smartApi.pathParams": "Path parameters",
  "pro.system.smartApi.query": "Query string",
  "pro.system.smartApi.queryHint": "One key=value per line. Empty values are skipped.",
  "pro.system.smartApi.body": "JSON body",
  "pro.system.smartApi.bodyInvalid": "The body isn't valid JSON.",
  "pro.system.smartApi.identity": "Identity",
  "pro.system.smartApi.identity.self": "My session",
  "pro.system.smartApi.identity.testUser": "Test as user",
  "pro.system.smartApi.identity.selfHint":
    "Both sides use your Pro session. smart-api receives it with x-auth-provider: user-service.",
  "pro.system.smartApi.identity.signedInAs": "Signed in as {email}",
  "pro.system.smartApi.identity.notSignedIn": "No test user signed in.",
  "pro.system.smartApi.identity.signIn": "Sign in test user",
  "pro.system.smartApi.identity.switch": "Switch user",
  "pro.system.smartApi.identity.signOut": "Clear",
  "pro.system.smartApi.send": "Send to both",
  "pro.system.smartApi.sending": "Sending…",
  "pro.system.smartApi.sendFailed": "The comparison request failed.",
  "pro.system.smartApi.smartSide": "smart-api (direct)",
  "pro.system.smartApi.proSide": "pro-api (legacy layer)",
  "pro.system.smartApi.status": "Status {status}",
  "pro.system.smartApi.noStatus": "No response",
  "pro.system.smartApi.duration": "{ms} ms",
  "pro.system.smartApi.statusMatch": "Status codes match",
  "pro.system.smartApi.statusMismatch": "Status codes differ",
  "pro.system.smartApi.shapeMatch": "Response shapes match",
  "pro.system.smartApi.shapeDifferences": "{count} shape differences",
  "pro.system.smartApi.shapeUnavailable": "Shape comparison needs a JSON body on both sides.",
  "pro.system.smartApi.headers": "Headers",
  "pro.system.smartApi.emptyBody": "(empty body)",
  "pro.system.smartApi.truncated": "Body truncated at 1,000,000 characters.",
  "pro.system.smartApi.noResults": "Choose an endpoint and send a request to see both responses side by side.",
  "pro.system.smartApi.diff.path": "Path",
  "pro.system.smartApi.diff.kind": "Difference",
  "pro.system.smartApi.diff.smart": "smart-api",
  "pro.system.smartApi.diff.pro": "pro-api",
  "pro.system.smartApi.diff.missing": "Missing in pro-api",
  "pro.system.smartApi.diff.added": "Only in pro-api",
  "pro.system.smartApi.diff.type": "Type differs",
  "pro.system.smartApi.confirmWrite.title": "Send a write to both systems?",
  "pro.system.smartApi.confirmWrite.body":
    "{{method}} {{path}} changes data. It will run once against smart-api and once against the pro-api legacy layer.",
  "pro.system.smartApi.confirmWrite.confirm": "Send write",
  "pro.system.smartApi.signIn.title": "Sign in a test user",
  "pro.system.smartApi.signIn.body":
    "Signs in on smart-api and through the pro-api login facade. The tokens are kept only in this page until you leave it.",
  "pro.system.smartApi.signIn.email": "Email",
  "pro.system.smartApi.signIn.password": "Password",
  "pro.system.smartApi.signIn.submit": "Sign in",
  "pro.system.smartApi.signIn.submitting": "Signing in…",
  "pro.system.smartApi.signIn.failed": "Sign-in failed. smart-api: {smart} · pro-api: {pro}",
  "pro.system.smartApi.section.flowTest": "Migration flow test",
  "pro.system.smartApi.flowTest.intro":
    "Creates a throwaway Smart user, then checks that the legacy layer rejects its old token, signs it in through the login facade with automatic Smart Link onboarding, and accepts the new token. Everything it creates is deleted afterwards.",
  "pro.system.smartApi.flowTest.run": "Run flow test",
  "pro.system.smartApi.flowTest.running": "Running the flow test… this can take up to a minute.",
  "pro.system.smartApi.flowTest.failed": "The flow test could not be run.",
  "pro.system.smartApi.flowTest.passed": "All steps passed",
  "pro.system.smartApi.flowTest.notPassed": "Some steps failed",
  "pro.system.smartApi.flowTest.testUser": "Test user: {email}",
  "pro.system.smartApi.flowTest.col.step": "Step",
  "pro.system.smartApi.flowTest.col.result": "Result",
  "pro.system.smartApi.flowTest.col.status": "HTTP",
  "pro.system.smartApi.flowTest.col.duration": "Time",
  "pro.system.smartApi.flowTest.col.detail": "Detail",
  "pro.system.smartApi.flowTest.result.ok": "Passed",
  "pro.system.smartApi.flowTest.result.fail": "Failed",
  "pro.system.smartApi.flowTest.result.skipped": "Skipped",
  "pro.system.smartApi.flowTest.step.smart-register": "Register a throwaway user on smart-api",
  "pro.system.smartApi.flowTest.step.claim-pushed": "Smart Link claim pushed to Pro",
  "pro.system.smartApi.flowTest.step.legacy-login": "Sign in on smart-api (legacy token)",
  "pro.system.smartApi.flowTest.step.legacy-token-rejected": "Legacy layer rejects the legacy token (401)",
  "pro.system.smartApi.flowTest.step.facade-login": "Sign in through the legacy login facade",
  "pro.system.smartApi.flowTest.step.token-lifetime": "Access token gets the mobile lifetime",
  "pro.system.smartApi.flowTest.step.onboarding-linked": "Smart Link onboarding linked the claim",
  "pro.system.smartApi.flowTest.step.pro-token-accepted": "Legacy layer accepts the new token",
  "pro.system.smartApi.flowTest.step.cleanup": "Clean up throwaway users",

  // Pre-existing gaps found while closing out missing-default enforcement.
  "pro.system.events.form.subscribable": "Subscribable",
  "pro.system.events.form.subscribableHelp":
    "Allow users to subscribe to notifications for this event.",

  // Definitions page quick filter (also seeded server-side via STARTER_PRO_UI —
  // kept here too so it renders correctly before that seed ever runs).
  "pro.system.localization.definitions.quickFilter.all": "All",
  "pro.system.localization.definitions.quickFilter.unsaved": "Unsaved",
  "pro.system.localization.definitions.quickFilter.empty": "Empty",
  "pro.system.localization.definitions.searchPlaceholder": "Search key or value…",
  "pro.system.localization.definitions.quickFilter.noMatches":
    "No strings match this filter.",

  // Merged from lion-energy-user-service's localization-seed-ui.ts -- CI
  // builds pro-web in isolation (no sibling repo checkout), so i18n:extract's
  // sibling-repo merge that covers these locally never runs there.
  "pro.components.componentId": "Component ID",
  "pro.components.componentType": "Component type",
  "pro.components.componentTypeRequired": "Component Type *",
  "pro.components.dataCatalog": "Data catalog",
  "pro.components.details": "Details",
  "pro.components.displayName": "Display name",
  "pro.components.firmwareVersion": "Firmware version",
  "pro.components.fwVersion": "FW version",
  "pro.components.hardwareVersion": "Hardware version",
  "pro.components.hwVersion": "HW version",
  "pro.components.inactive": "INACTIVE",
  "pro.components.index": "Index",
  "pro.components.iotComponentId": "IoT component ID",
  "pro.components.manufacturer": "Manufacturer",
  "pro.components.placeholder.displayName": "e.g. Main PCS",
  "pro.components.placeholder.iotComponentId": "AWS IoT component UUID",
  "pro.components.placeholder.serial": "Manufacturer S/N",
  "pro.components.productId": "Product ID",
  "pro.components.removeComponent": "Remove component",
  "pro.dataTransfer.checkpointMatches": "Current data already matches this checkpoint.",
  "pro.dataTransfer.csvPreview": "CSV preview",
  "pro.dataTransfer.exportFormat": "Export format",
  "pro.dataTransfer.exportScope": "Export scope",
  "pro.dataTransfer.fileMatchesCurrent": "The file matches the current data.",
  "pro.dataTransfer.jsonOutput": "JSON output",
  "pro.dataTransfer.media": "Media",
  "pro.dataTransfer.mediaInZip": "Media in ZIP",
  "pro.dataTransfer.nothingToExportScope": "Nothing to export for this scope.",
  "pro.dataTransfer.removeFile": "Remove file",
  "pro.dataTransfer.restorePreview": "Restore preview",
  "pro.dataTransfer.zipManifest": "ZIP manifest",
  "pro.devCaptures.clearAll": "Clear all",
  "pro.devCaptures.emailCount": "{count, plural, one {# email} other {# emails}}",
  "pro.devCaptures.emailPreview": "Email preview",
  "pro.devCaptures.emails": "Emails",
  "pro.devCaptures.loading": "Loading…",
  "pro.devCaptures.messageCount": "{count, plural, one {# message} other {# messages}}",
  "pro.devCaptures.meta": "Meta:",
  "pro.devCaptures.noContent": "No content available",
  "pro.devCaptures.noEmails": "No captured emails",
  "pro.devCaptures.noEmailsHint": "Emails sent in dev mode will appear here",
  "pro.devCaptures.noSms": "No captured SMS messages",
  "pro.devCaptures.noSmsHint": "SMS sent in dev mode will appear here",
  "pro.devCaptures.refresh": "Refresh",
  "pro.devCaptures.selectEmail": "Select an email to view its contents",
  "pro.devCaptures.selectMessage": "Select a message to view its contents",
  "pro.devCaptures.sent": "Sent:",
  "pro.devCaptures.sms": "SMS",
  "pro.devCaptures.subject": "Subject:",
  "pro.devCaptures.title": "Dev Captures",
  "pro.devCaptures.to": "To:",
  "pro.fleet.alerts.acknowledge": "Acknowledge",
  "pro.fleet.alerts.acknowledged": "Acknowledged",
  "pro.fleet.alerts.acknowledgeTitle": "Acknowledge this alert",
  "pro.fleet.alerts.allClear": "All clear",
  "pro.fleet.alerts.badgeAlert": "Alert",
  "pro.fleet.alerts.badgeFault": "Fault",
  "pro.fleet.alerts.badgeIncident": "Incident",
  "pro.fleet.alerts.colDevice": "Device",
  "pro.fleet.alerts.colDuration": "Duration",
  "pro.fleet.alerts.colFirstSeen": "First seen",
  "pro.fleet.alerts.colMessage": "Message",
  "pro.fleet.alerts.colSite": "Site",
  "pro.fleet.alerts.colType": "Type",
  "pro.fleet.alerts.criticalCount": "{count} critical",
  "pro.fleet.alerts.emptyActive": "All clear — no active alerts",
  "pro.fleet.alerts.emptyActiveSub": "All sites in your fleet are operating normally.",
  "pro.fleet.alerts.emptyFiltered": "No alerts match your filters",
  "pro.fleet.alerts.emptyFilteredSub": "Try adjusting your filters or search query.",
  "pro.fleet.alerts.emptyNone": "No alerts found",
  "pro.fleet.alerts.infoCount": "{count} info",
  "pro.fleet.alerts.manageSubscriptions": "Manage Subscriptions",
  "pro.fleet.alerts.moreAlerts": "+{count, plural, one {# more alert} other {# more alerts}}",
  "pro.fleet.alerts.newAlertsCount": "{count, plural, one {# new alert} other {# new alerts}}",
  "pro.fleet.alerts.newAlertsSuffix": "received since you last loaded this page.",
  "pro.fleet.alerts.noActive": "No active alerts",
  "pro.fleet.alerts.refresh": "Refresh",
  "pro.fleet.alerts.searchPlaceholder": "Search alerts, sites…",
  "pro.fleet.alerts.severityAll": "All",
  "pro.fleet.alerts.severityCritical": "Critical",
  "pro.fleet.alerts.severityInfo": "Info",
  "pro.fleet.alerts.severityWarning": "Warning",
  "pro.fleet.alerts.statusAcknowledged": "Acknowledged",
  "pro.fleet.alerts.statusActive": "Active",
  "pro.fleet.alerts.statusAll": "All",
  "pro.fleet.alerts.title": "Fleet Alerts",
  "pro.fleet.alerts.typeAlerts": "Alerts",
  "pro.fleet.alerts.typeAll": "All types",
  "pro.fleet.alerts.typeFaults": "Faults",
  "pro.fleet.alerts.typeIncidents": "Incidents",
  "pro.fleet.alerts.viewAll": "View all →",
  "pro.fleet.alerts.warningCount": "{count} warning",
  "pro.fleet.programsTitle": "Fleet Programs",
  "pro.fleet.reportsTitle": "Fleet Reports",
  "pro.fleet.sitesTitle": "Fleet Sites",
  "pro.orgs.aboutAutoApprovedPatterns": "About auto approved email patterns",
  "pro.orgs.address": "Address",
  "pro.orgs.cancelMembershipRequest": "Cancel membership request",
  "pro.orgs.city": "City",
  "pro.orgs.clearSearch": "Clear search",
  "pro.orgs.contact": "Contact",
  "pro.orgs.country": "Country",
  "pro.orgs.countryCode": "Country code",
  "pro.orgs.defaultOrganizationTitle": "Your default organization in Lion Pro",
  "pro.orgs.description": "Description",
  "pro.orgs.descriptionPlaceholder": "Brief description of your organization",
  "pro.orgs.disabledBanner": "This organization is disabled and members cannot access it",
  "pro.orgs.email": "Email",
  "pro.orgs.emailAddress": "Email address",
  "pro.orgs.emailPattern": "Email pattern",
  "pro.orgs.emptySubtitle": "Try adjusting your search query.",
  "pro.orgs.inviteMember": "Invite member",
  "pro.orgs.loading": "Loading…",
  "pro.orgs.loadingOrganizations": "Loading organizations…",
  "pro.orgs.logoPlaceholder": "Click or drag a logo here",
  "pro.orgs.memberRoles": "Member Roles",
  "pro.orgs.membership": "Membership",
  "pro.orgs.membershipEmailPatterns": "Membership Email Patterns",
  "pro.orgs.mockDataBanner": "This organization was created from mock data",
  "pro.orgs.nameRequired": "Name *",
  "pro.orgs.noPatternsYet": "No patterns added yet.",
  "pro.orgs.noRolesAvailable": "No roles available",
  "pro.orgs.organizationDetails": "Organization details",
  "pro.orgs.phone": "Phone",
  "pro.orgs.phoneNumber": "Phone number",
  "pro.orgs.role": "Role",
  "pro.orgs.roleColor": "Color",
  "pro.orgs.roleLabel": "Label",
  "pro.orgs.searchAriaLabel": "Search organizations",
  "pro.orgs.searchPlaceholder": "Search by name, city, or description…",
  "pro.orgs.signInRequired": "Sign in required to view organizations.",
  "pro.orgs.state": "State",
  "pro.orgs.statusDisabled": "Disabled",
  "pro.orgs.statusPending": "Pending",
  "pro.orgs.unsavedChanges": "Unsaved changes",
  "pro.orgs.viewOrganization": "View organization",
  "pro.orgs.zip": "Zip",
  "pro.products.accessLevel": "Access level",
  "pro.products.address": "Address",
  "pro.products.address2": "Address 2",
  "pro.products.addressLine2": "Address line 2",
  "pro.products.alerts.activeSimulatedAria": "Active simulated alerts",
  "pro.products.alerts.activeTitle": "Active alerts",
  "pro.products.alerts.bulkActionsAria": "Bulk alert actions",
  "pro.products.alerts.exception": "Exception",
  "pro.products.alerts.filterListsAria": "Alert filter lists",
  "pro.products.alerts.loading": "Loading alerts…",
  "pro.products.alerts.nextPage": "Next page",
  "pro.products.alerts.previousPage": "Previous page",
  "pro.products.alerts.recentEmptySubtitle": "Alerts from the last 7 days will appear here",
  "pro.products.alerts.searchAria": "Search alerts",
  "pro.products.alerts.searchByNameOrCode": "Search alerts by name or code…",
  "pro.products.alerts.searchPlaceholder": "Search alerts…",
  "pro.products.alerts.severitiesAria": "Alert severities",
  "pro.products.alerts.simulate": "Simulate Alert",
  "pro.products.alerts.subscribed": "Subscribed",
  "pro.products.assetTag": "Asset tag",
  "pro.products.city": "City",
  "pro.products.components": "Components",
  "pro.products.country": "Country",
  "pro.products.created": "Created",
  "pro.products.dangerZone": "Danger zone",
  "pro.products.decommissionProduct": "Decommission product",
  "pro.products.deleteImage": "Delete image",
  "pro.products.description": "Description",
  "pro.products.dragDropImages": "Drag & drop product images here",
  "pro.products.emptySubtitle": "Create your first product to get started.",
  "pro.products.expiresIn": "Expires in",
  "pro.products.gridOffGrid": "Off Grid",
  "pro.products.gridOnGrid": "On Grid",
  "pro.products.gridType": "Grid type",
  "pro.products.historicalData": "Historical Data",
  "pro.products.identifiers": "Identifiers",
  "pro.products.images": "Images",
  "pro.products.insights.analyzing": "Analyzing the data…",
  "pro.products.insights.analyzingAlert": "Analyzing this alert…",
  "pro.products.insights.basedOn": "Based on",
  "pro.products.insights.confidence.high": "high confidence",
  "pro.products.insights.confidence.low": "low confidence",
  "pro.products.insights.confidence.medium": "medium confidence",
  "pro.products.insights.contactLionIf": "Contact Lion if",
  "pro.products.insights.dismiss": "Dismiss",
  "pro.products.insights.dismissAria": "Dismiss insight",
  "pro.products.insights.empty":
    "Nothing needs your attention. New Insights appear here when an alert opens or something in the data changes.",
  "pro.products.insights.error.busy": "The analysis is still being prepared. Try again in a minute.",
  "pro.products.insights.error.generic": "Couldn't load the analysis. Try again shortly.",
  "pro.products.insights.error.noGuidance":
    "Lion hasn't published guidance for this alert yet. Lion support can help you work out what caused it.",
  "pro.products.insights.error.paused":
    "Insight analysis is paused right now. Call Lion support at {phone} if you need help with this alert.",
  "pro.products.insights.feedback.helpful": "Helpful",
  "pro.products.insights.feedback.notHelpful": "Not helpful",
  "pro.products.insights.feedback.prompt": "Was this helpful?",
  "pro.products.insights.hideDetails": "Hide details",
  "pro.products.insights.hideResolved": "Hide {count} resolved",
  "pro.products.insights.kind.alert_explanation": "Alert explained",
  "pro.products.insights.kind.optimization": "Savings",
  "pro.products.insights.kind.performance": "Performance",
  "pro.products.insights.kind.resilience": "Resilience",
  "pro.products.insights.loadFailed": "Couldn't load Insights. Refresh the page to try again.",
  "pro.products.insights.loading": "Loading insights…",
  "pro.products.insights.noAction": "No action needed.",
  "pro.products.insights.noGuidanceForAlert":
    "We don't have guidance for this alert yet. Lion support can help you work out what caused it.",
  "pro.products.insights.occurrences":
    "{count, plural, one {# occurrence} other {# occurrences}} merged into this Insight",
  "pro.products.insights.panelTitle": "Lion Insights",
  "pro.products.insights.resolved": "Resolved",
  "pro.products.insights.showDetails": "Why, and what to do",
  "pro.products.insights.showFewer": "Show fewer",
  "pro.products.insights.showMore": "Show {count} more",
  "pro.products.insights.showResolved": "Show {count} resolved",
  "pro.products.insights.subtitle":
    "{count, plural, =0 {All clear} one {# needs attention} other {# need attention}}",
  "pro.products.insights.support.button": "Still need help",
  "pro.products.insights.supportModal.attached":
    "Attached automatically: this analysis, the alert history and the telemetry behind it.",
  "pro.products.insights.supportModal.cancel": "Cancel",
  "pro.products.insights.supportModal.impact": "How is the system doing right now?",
  "pro.products.insights.supportModal.impact.degraded": "Partly working (reduced power or some features not working)",
  "pro.products.insights.supportModal.impact.down": "Not working (no power or completely offline)",
  "pro.products.insights.supportModal.impact.working": "Working normally, I have a question",
  "pro.products.insights.supportModal.intro":
    "Lion support will review this with the details below, so you won't have to explain it from the start.",
  "pro.products.insights.supportModal.notes": "Anything else we should know?",
  "pro.products.insights.supportModal.notesPlaceholder":
    "For example, when it started, anything that changed on site, or error lights you can see",
  "pro.products.insights.supportModal.optional": "(optional)",
  "pro.products.insights.supportModal.phone": "Best number to reach you",
  "pro.products.insights.supportModal.phoneHint": "Leave blank to be contacted at the email on your account.",
  "pro.products.insights.supportModal.submit": "Send to Lion support",
  "pro.products.insights.supportModal.title": "Ask Lion support for help",
  "pro.products.insights.supportModal.tried": "What have you already tried?",
  "pro.products.insights.support.contact": "Contact Lion support",
  "pro.products.insights.support.failed": "Couldn't send the request. Call Lion support at {phone}.",
  "pro.products.insights.support.opened": "Support request {reference} opened with this Insight attached",
  "pro.products.insights.support.recorded":
    "Request {reference} recorded. Call Lion support at {phone} and give this reference.",
  "pro.products.insights.title": "Insights",
  "pro.products.insights.urgency.critical": "Critical",
  "pro.products.insights.urgency.high": "Act soon",
  "pro.products.insights.urgency.info": "FYI",
  "pro.products.insights.urgency.low": "Opportunity",
  "pro.products.insights.urgency.medium": "Worth a look",
  "pro.products.insights.usage.details": "Details:",
  "pro.products.insights.usage.headline": "Headline:",
  "pro.products.insights.usage.notGenerated": "not generated yet",
  "pro.products.insights.usage.summary":
    "AI usage this month for this product: {headlines} headlines ({templated} templated) {headlineCost} · {details} full analyses {detailsCost} · total est. {total}. {skipped} headlines were never opened, which avoided about {avoided} in analysis.",
  "pro.products.insights.usage.superAdminOnly": "Visible to super-admins only",
  "pro.products.insights.usage.template": "template (no model call)",
  "pro.products.insights.usage.unrecorded": "generated (usage not recorded)",
  "pro.products.insights.viewData": "View data",
  "pro.products.insights.whatThisMeans": "What this means",
  "pro.products.insights.whatYouCanDo": "What you can do",
  "pro.products.insights.why": "Why we think this",
  "pro.products.inviteToProduct": "Invite to product",
  "pro.products.iotThingName": "IoT thing name",
  "pro.products.latitude": "Latitude",
  "pro.products.loadingAccess": "Loading access…",
  "pro.products.location": "Location",
  "pro.products.longitude": "Longitude",
  "pro.products.macAddress": "MAC address",
  "pro.products.model": "Model",
  "pro.products.modelNumber": "Model number",
  "pro.products.modelsEmptySubtitle": "Add a product model to get started.",
  "pro.products.modelsTitle": "Product Models",
  "pro.products.nameRequired": "Name *",
  "pro.products.noDatapoints": "No datapoints",
  "pro.products.noDatapointsAvailable": "No datapoints available.",
  "pro.products.noMatchingTelemetryKeys": "No matching telemetry keys.",
  "pro.products.notSet": "Not set",
  "pro.products.overview": "Overview",
  "pro.products.permanentlyDeleteProduct": "Permanently delete product",
  "pro.products.placeholder.iotThingName": "Smart API product UUID (set at commissioning)",
  "pro.products.placeholder.productName": "e.g. Smith Residence Sanctuary",
  "pro.products.placeholder.smartUuid": "Paste the Smart product UUID",
  "pro.products.productAccess": "Product Access",
  "pro.products.productLine": "Product line",
  "pro.products.productModel": "Product model",
  "pro.products.productNameRequired": "Product name *",
  "pro.products.pvCapacityW": "PV capacity (W)",
  "pro.products.realTimeData": "Real-time Data",
  "pro.products.runningFor": "Running for",
  "pro.products.searchDatapoints": "Search datapoints…",
  "pro.products.serialNumber": "Serial number",
  "pro.products.signInRequired": "Please sign in to view products.",
  "pro.products.simBadge": "SIM",
  "pro.products.simulated": "Simulated",
  "pro.products.simulatedProduct": "Simulated product",
  "pro.products.simulatedProductHint": "Uses physics-driven simulation instead of real IoT data",
  "pro.products.simulatedTelemetryBanner": "This product uses simulated telemetry instead of a live device",
  "pro.products.simulation": "Simulation",
  "pro.products.smartUuid": "Smart UUID",
  "pro.products.solarCapacity": "Solar capacity",
  "pro.products.solarCapacityW": "Solar capacity (W)",
  "pro.products.solarGroundMount": "Ground Mount",
  "pro.products.solarInstallLocation": "Solar install location",
  "pro.products.solarRoofTop": "Roof Top",
  "pro.products.source": "Source",
  "pro.products.state": "State",
  "pro.products.status": "Status",
  "pro.products.subscribers": "Subscribers",
  "pro.products.subscriptions.addEvent": "Add Event",
  "pro.products.subscriptions.addSubscription": "Add Subscription",
  "pro.products.subscriptions.allEventsAdded": "All available notifications have been added",
  "pro.products.subscriptions.badge.added": "Added",
  "pro.products.subscriptions.badge.removed": "Removed",
  "pro.products.subscriptions.channel.email": "Email",
  "pro.products.subscriptions.channel.emailUnavailable": "Email not verified for this user",
  "pro.products.subscriptions.channel.push": "Push notifications",
  "pro.products.subscriptions.channel.sms": "SMS",
  "pro.products.subscriptions.channel.smsNoPhone": "No phone number on this user",
  "pro.products.subscriptions.channel.smsUnavailable": "Phone not verified for this user",
  "pro.products.subscriptions.channel.web": "Web",
  "pro.products.subscriptions.edit": "Edit subscriptions",
  "pro.products.subscriptions.error.load": "Failed to load subscriptions",
  "pro.products.subscriptions.error.loadSubscribers": "Failed to load subscribers",
  "pro.products.subscriptions.error.save": "Failed to save subscriptions",
  "pro.products.subscriptions.event": "event",
  "pro.products.subscriptions.events": "Events",
  "pro.products.subscriptions.goToProductAccess": "Go to product access",
  "pro.products.subscriptions.history": "History",
  "pro.products.subscriptions.history.emptySubtitle": "No notification history for this user and product yet.",
  "pro.products.subscriptions.history.emptyTitle": "No notification history",
  "pro.products.subscriptions.history.subtitle": "Recent notification activity for {user} on {product}",
  "pro.products.subscriptions.history.title": "Notification history",
  "pro.products.subscriptions.modal.title": "Product subscriptions",
  "pro.products.subscriptions.noEvents": "No events configured",
  "pro.products.subscriptions.noNotifications": "No notifications configured yet.",
  "pro.products.subscriptions.noProductAccess": "This user does not have access to this product. Grant access before managing subscriptions.",
  "pro.products.subscriptions.noSubscribers": "No one is subscribed to this product yet.",
  "pro.products.subscriptions.notification": "notification",
  "pro.products.subscriptions.notifications": "Notifications",
  "pro.products.subscriptions.notificationsCount": "notifications",
  "pro.products.subscriptions.noUsersFound": "No users found",
  "pro.products.subscriptions.product": "Product",
  "pro.products.subscriptions.remove": "Remove",
  "pro.products.subscriptions.searchUser": "Search for a user",
  "pro.products.subscriptions.selectEvent": "Select a notification",
  "pro.products.subscriptions.selectUserFirst": "Select a user to manage their subscriptions.",
  "pro.products.subscriptions.severity": "Severity",
  "pro.products.subscriptions.subscribe": "Subscribe",
  "pro.products.subscriptions.undoRemove": "Undo remove",
  "pro.products.subscriptions.user": "User",
  "pro.products.suggested": "Suggested",
  "pro.products.system": "System",
  "pro.products.telemetry": "Telemetry",
  "pro.products.timezone": "Timezone",
  "pro.products.unsavedChanges": "Unsaved changes",
  "pro.products.updated": "Updated",
  "pro.products.zip": "ZIP",
  "pro.productSettings.loadingSettings": "Loading settings…",
  "pro.productSettings.noSettingsFound": "No settings found for this shadow.",
  "pro.productSettings.noShadows": "No settings shadows are available for this product.",
  "pro.productSettings.off": "Off",
  "pro.productSettings.on": "On",
  "pro.productSettings.options": "Options:",
  "pro.productSettings.resetUnsaved": "Reset all unsaved changes",
  "pro.productSettings.scrollToFirstUnsaved": "Scroll to first unsaved change",
  "pro.productSettings.settings": "Settings",
  "pro.productSettings.shadowJson": "Shadow JSON",
  "pro.productSettings.viewShadowJson": "Click to view shadow JSON",
  "pro.profile.accountDetails": "Account details",
  "pro.profile.addPhone": "Add phone number",
  "pro.profile.cancelRequest": "Cancel request",
  "pro.profile.certifications": "Certifications",
  "pro.profile.changePasswordOnGoogle": "Change password on Google →",
  "pro.profile.confirmNewPassword": "Confirm new password",
  "pro.profile.contactMethods": "Contact Methods",
  "pro.profile.countryCode": "Country code",
  "pro.profile.createOrganization": "Create new organization",
  "pro.profile.currentPassword": "Current password",
  "pro.profile.email": "Email",
  "pro.profile.emailManagedHint": "Email address is managed by your identity provider and cannot be changed here",
  "pro.profile.failedJoinOrg": "Failed to join organization",
  "pro.profile.failedLeaveOrg": "Failed to leave organization",
  "pro.profile.failedUpdate": "Failed to update profile",
  "pro.profile.failedUpdatePassword": "Failed to update password",
  "pro.profile.firstName": "First name",
  "pro.profile.googleProfilePhoto": "Google profile photo",
  "pro.profile.join": "Join",
  "pro.profile.joinOrganization": "Join an organization",
  "pro.profile.lastName": "Last name",
  "pro.profile.leaveOrganization": "Leave organization",
  "pro.profile.leaveOrgConfirmPrefix": "If you confirm, you will no longer be a member of",
  "pro.profile.leaveOrgModalTitle": "Leave organization?",
  "pro.profile.loading": "Loading profile…",
  "pro.profile.memberSince": "Member since {date}",
  "pro.profile.newPassword": "New password",
  "pro.profile.notVerified": "Not verified",
  "pro.profile.organizations": "Organizations",
  "pro.profile.orgColumn": "Organization",
  "pro.profile.orgsEmptySubtitle": "Join or create an organization to get started.",
  "pro.profile.orgsEmptyTitle": "No organizations yet.",
  "pro.profile.passwordsMismatch": "New passwords do not match.",
  "pro.profile.passwordUpdated": "Password updated.",
  "pro.profile.phone": "Phone",
  "pro.profile.phoneNumber": "Phone number",
  "pro.profile.removePhone": "Remove phone",
  "pro.profile.saveProfile": "Save profile",
  "pro.profile.searchOrganizations": "Search organizations…",
  "pro.profile.security": "Security",
  "pro.profile.statusAvailable": "Available",
  "pro.profile.statusColumn": "Status",
  "pro.profile.statusMember": "Member",
  "pro.profile.statusPending": "Pending",
  "pro.profile.subtitle": "View and update your account information.",
  "pro.profile.title": "Profile",
  "pro.profile.unsavedChanges": "Unsaved changes",
  "pro.profile.updated": "Profile updated.",
  "pro.profile.updatePassword": "Update password",
  "pro.profile.verified": "Verified",
  "pro.profile.verifyEmail": "Verify email",
  "pro.profile.verifyPhone": "Verify phone",
  "pro.profile.you": "You",
  "pro.realtime.connection.reconnecting": "Reconnecting…",
  "pro.realtime.connection.unavailable": "Live updates unavailable",
  "pro.realtime.connection.unavailableHint": "The app can't reach the real-time server. Changes still save, but other open sessions won't update until the connection returns.",
  "pro.site.overviewTitle": "Site Overview",
  "pro.site.reportsTitle": "Site Reports",
  "pro.system.capture.clearAll": "Clear all",
  "pro.system.capture.emailCount": "{count, plural, one {# email} other {# emails}}",
  "pro.system.capture.emailPreview": "Email preview",
  "pro.system.capture.error.load": "Failed to load captured messages",
  "pro.system.capture.loading": "Loading…",
  "pro.system.capture.messageCount": "{count, plural, one {# message} other {# messages}}",
  "pro.system.capture.messages": "Messages",
  "pro.system.capture.metaTitle": "Message metadata",
  "pro.system.capture.noContent": "No content available",
  "pro.system.capture.noEmails": "No captured emails",
  "pro.system.capture.noEmailsHint": "Emails sent in capture mode will appear here",
  "pro.system.capture.noMeta": "No metadata for this message",
  "pro.system.capture.noSms": "No captured SMS messages",
  "pro.system.capture.noSmsHint": "SMS sent in capture mode will appear here",
  "pro.system.capture.refresh": "Refresh",
  "pro.system.capture.selectEmail": "Select an email to view its contents",
  "pro.system.capture.selectMessage": "Select a message to view its contents",
  "pro.system.capture.sent": "Sent",
  "pro.system.capture.subject": "Subject",
  "pro.system.capture.subtitle": "Dev-captured emails and SMS for local debugging",
  "pro.system.capture.tab.email": "Email",
  "pro.system.capture.tab.sms": "SMS",
  "pro.system.capture.tabsAria": "Capture channel",
  "pro.system.capture.title": "Notification capture",
  "pro.system.capture.to": "To",
  "pro.system.capture.viewMeta": "View metadata",
  "pro.system.certification.col.email": "Email",
  "pro.system.certification.col.name": "Name",
  "pro.system.certification.col.slug": "Slug",
  "pro.system.certification.col.status": "Status",
  "pro.system.certification.col.title": "Title",
  "pro.system.certification.confirm.deleteModuleBody": "Delete \"{title}\"? This cannot be undone.",
  "pro.system.certification.confirm.deleteModuleTitle": "Delete module",
  "pro.system.certification.error.loadModules": "Failed to load modules",
  "pro.system.certification.error.loadTechnicians": "Failed to load technicians",
  "pro.system.certification.modules.add": "Add module",
  "pro.system.certification.modules.emptySubtitle": "No certification modules have been created yet.",
  "pro.system.certification.modules.emptyTitle": "No modules",
  "pro.system.certification.modules.title": "Modules",
  "pro.system.certification.status.active": "Active",
  "pro.system.certification.status.inactive": "Inactive",
  "pro.system.certification.subtitle": "Manage certification modules, trainings, and technician progress.",
  "pro.system.certification.technicians.empty": "No technicians have enrolled in any certification modules yet.",
  "pro.system.certification.technicians.emptySubtitle": "No matching technicians.",
  "pro.system.certification.technicians.emptyTitle": "No technicians",
  "pro.system.certification.technicians.search": "Search technicians…",
  "pro.system.certification.technicians.title": "Technicians",
  "pro.system.certification.technicians.lastSignIn": "Last sign in: {date}",
  "pro.system.certification.technicians.neverSignedIn": "Never signed in",
  "pro.system.certification.title": "Certifications",
  "pro.system.certification.training.addTraining": "Add training",
  "pro.system.certification.training.error.invalidOptionalField": "One or more optional fields were sent empty. Video, thumbnail, and transcript can be left blank.",
  "pro.system.certification.training.error.invalidVideoUrl": "Enter a valid video URL, or leave the video field blank.",
  "pro.system.certification.training.error.removeVideo": "Failed to remove video.",
  "pro.system.certification.training.error.save": "Failed to save training.",
  "pro.system.certification.training.error.saveVideo": "Failed to save video.",
  "pro.system.certification.training.error.uploadThumbnail": "Failed to upload video thumbnail.",
  "pro.system.certification.training.error.uploadVideo": "Failed to upload video.",
  "pro.system.certification.training.form.section": "Section",
  "pro.system.certification.training.form.sectionPlaceholder": "e.g. Installation",
  "pro.system.certification.training.form.stepRequired": "Step # *",
  "pro.system.certification.training.form.title": "Title",
  "pro.system.certification.training.form.titlePlaceholder": "e.g. Mounting the enclosure",
  "pro.system.certification.training.form.transcript": "Transcript",
  "pro.system.certification.training.form.transcriptPlaceholder": "Optional video transcript / notes",
  "pro.system.certification.training.form.videoCommentary": "Video commentary",
  "pro.system.certification.training.form.videoCommentaryPlaceholder": "Optional note shown below the video, before the quiz buttons",
  "pro.system.certification.training.modal.add": "Add Training",
  "pro.system.certification.training.modal.edit": "Edit Training",
  "pro.system.certification.training.questions.add": "Add question",
  "pro.system.certification.training.questions.addChoice": "Add choice",
  "pro.system.certification.training.questions.choiceN": "Choice {n}",
  "pro.system.certification.training.questions.empty": "No questions yet.",
  "pro.system.certification.training.questions.markCorrect": "Mark as correct answer",
  "pro.system.certification.training.questions.questionN": "Question {n}",
  "pro.system.certification.training.questions.questionText": "Question text",
  "pro.system.certification.training.questions.removeChoice": "Remove choice",
  "pro.system.certification.training.questions.removeQuestion": "Remove question",
  "pro.system.certification.training.questions.title": "Questions",
  "pro.system.certification.training.saveChanges": "Save changes",
  "pro.system.certification.training.validation.choiceTextRequired": "Every choice needs text (default language).",
  "pro.system.certification.training.validation.duplicateQuestionText": "Each question must have unique text (default language).",
  "pro.system.certification.training.validation.minChoices": "Every question needs at least 2 choices.",
  "pro.system.certification.training.validation.questionTextRequired": "Every question needs text (default language).",
  "pro.system.certification.training.validation.stepRequired": "Step number is required.",
  "pro.system.certification.training.validation.stepWholeNumber": "Step number must be a whole number.",
  "pro.system.certification.training.validation.titleRequired": "Title is required (default language).",
  "pro.system.certification.training.video": "Training video",
  "pro.system.rewardRules.title": "Reward Rules",
  "pro.system.rewardRules.createButton": "Create rule",
  "pro.system.rewardRules.empty": "No reward rules have been added.",
  "pro.system.rewardRules.error.load": "Failed to load reward rules",
  "pro.system.rewardRules.requirementsLabel": "Requirements",
  "pro.system.rewardRules.rewardsLabel": "Rewards",
  "pro.system.rewardRules.enabledBadge": "Enabled",
  "pro.system.rewardRules.disabledBadge": "Disabled",
  "pro.system.rewardRules.modal.createTitle": "Create Reward Rule",
  "pro.system.rewardRules.modal.editTitle": "Edit Reward Rule",
  "pro.system.rewardRules.modal.nameLabel": "Name",
  "pro.system.rewardRules.modal.namePlaceholder": "e.g. Sanctuary Master Certification",
  "pro.system.rewardRules.modal.enabledLabel": "Enabled",
  "pro.system.rewardRules.modal.requirementsSection": "Requirement modules",
  "pro.system.rewardRules.modal.requirementsHint": "Users must fully complete every module below to receive the rewards.",
  "pro.system.rewardRules.modal.filterModulesPlaceholder": "Filter modules…",
  "pro.system.rewardRules.modal.noModules": "No certification modules found.",
  "pro.system.rewardRules.modal.rewardsSection": "Rewards",
  "pro.system.rewardRules.modal.rolesSubsection": "Roles",
  "pro.system.rewardRules.modal.noRoles": "No global roles found.",
  "pro.system.rewardRules.modal.permissionsSubsection": "Permissions",
  "pro.system.rewardRules.modal.filterPermissionsPlaceholder": "Filter permissions…",
  "pro.system.rewardRules.modal.noPermissions": "No permissions found.",
  "pro.system.rewardRules.modal.nameRequired": "Name is required",
  "pro.system.rewardRules.modal.requirementsRequired": "At least one requirement module is required",
  "pro.system.rewardRules.modal.rewardsRequired": "At least one reward role or permission is required",
  "pro.system.rewardRules.modal.saveError": "Failed to save reward rule",
  "pro.system.rewardRules.modal.deleteError": "Failed to delete reward rule",
  "pro.system.rewardRules.modal.delete": "Delete rule",
  "pro.system.rewardRules.modal.retroactiveToastBody": "Granted to {count} already-qualified user(s)",
  "pro.system.roadmap.title": "Roadmap",
  "pro.system.roadmap.add": "Add item",
  "pro.system.roadmap.signInRequired": "Sign in to view the roadmap.",
  "pro.system.roadmap.status.done": "Done",
  "pro.system.roadmap.status.in_progress": "In Progress",
  "pro.system.roadmap.status.planned": "Planned",
  "pro.system.roadmap.status.known_bug": "Known Bugs",
  "pro.system.roadmap.section.empty": "Drop items here",
  "pro.system.roadmap.priority.p1": "P1",
  "pro.system.roadmap.priority.p2": "P2",
  "pro.system.roadmap.priority.p3": "P3",
  "pro.system.roadmap.complexity.s": "S",
  "pro.system.roadmap.complexity.m": "M",
  "pro.system.roadmap.complexity.l": "L",
  "pro.system.roadmap.complexity.xl": "XL",
  "pro.system.roadmap.origin.roadmap": "Roadmap",
  "pro.system.roadmap.origin.new": "New",
  "pro.system.roadmap.origin.added": "Added",
  "pro.system.roadmap.field.name": "Name",
  "pro.system.roadmap.field.description": "Description",
  "pro.system.roadmap.field.status": "Status",
  "pro.system.roadmap.field.priority": "Priority",
  "pro.system.roadmap.field.complexity": "Complexity",
  "pro.system.roadmap.field.category": "Category",
  "pro.system.roadmap.field.origin": "Origin",
  "pro.system.roadmap.field.smartSource": "Smart source",
  "pro.system.roadmap.field.none": "None",
  "pro.system.roadmap.field.createdBy": "Created by",
  "pro.system.roadmap.field.created": "Created",
  "pro.system.roadmap.card.createdBySeed": "Roadmap import",
  "pro.system.roadmap.card.drag": "Drag to move",
  "pro.system.roadmap.card.edit": "Edit",
  "pro.system.roadmap.card.delete": "Delete",
  "pro.system.roadmap.card.noDescription": "No description",
  "pro.system.roadmap.modal.createTitle": "Add Roadmap Item",
  "pro.system.roadmap.modal.editTitle": "Edit Roadmap Item",
  "pro.system.roadmap.modal.nameRequired": "Name is required",
  "pro.system.roadmap.modal.saveError": "Failed to save roadmap item",
  "pro.system.roadmap.modal.cancel": "Cancel",
  "pro.system.roadmap.modal.save": "Save",
  "pro.system.roadmap.modal.create": "Create",
  "pro.system.roadmap.error.load": "Failed to load roadmap",
  "pro.system.roadmap.error.move": "Could not move item",
  "pro.system.roadmap.error.delete": "Could not delete item",
  "pro.system.roadmap.delete.title": "Delete roadmap item?",
  "pro.system.roadmap.delete.body": "\"{name}\" will be removed from the roadmap. This cannot be undone.",
  "pro.system.roadmap.delete.confirm": "Delete",
  "pro.system.dataCatalog.access.admin": "admin",
  "pro.system.dataCatalog.access.any": "any",
  "pro.system.dataCatalog.access.owner": "owner",
  "pro.system.dataCatalog.access.technician": "technician",
  "pro.system.dataCatalog.action.addEnumValue": "+ Enum value",
  "pro.system.dataCatalog.addDatapoint": "Add datapoint",
  "pro.system.dataCatalog.addSetting": "Add setting",
  "pro.system.dataCatalog.alerts.definitionsTitle": "Alert definitions",
  "pro.system.dataCatalog.alerts.emptySubtitle": "Add an alert definition to get started",
  "pro.system.dataCatalog.alerts.form.code": "Code",
  "pro.system.dataCatalog.alerts.form.codePlaceholder": "E001",
  "pro.system.dataCatalog.alerts.form.namePlaceholder": "Alert Name",
  "pro.system.dataCatalog.alerts.type.alarm": "Alarm",
  "pro.system.dataCatalog.alerts.type.fault": "Fault",
  "pro.system.dataCatalog.aria.actions": "Catalog actions",
  "pro.system.dataCatalog.aria.tabs": "Data catalog tabs",
  "pro.system.dataCatalog.badge.inactive": "Inactive",
  "pro.system.dataCatalog.col.description": "Description",
  "pro.system.dataCatalog.col.firmware": "Firmware",
  "pro.system.dataCatalog.col.name": "Name",
  "pro.system.dataCatalog.col.slug": "Slug",
  "pro.system.dataCatalog.confirm.activate": "Activate",
  "pro.system.dataCatalog.confirm.activateBody": "This catalog will be used for enrichment for all products that resolve to it.",
  "pro.system.dataCatalog.confirm.activateTitle": "Activate \"{name}\"?",
  "pro.system.dataCatalog.confirm.deactivate": "Deactivate",
  "pro.system.dataCatalog.confirm.deactivateBody": "This catalog will no longer be used for enrichment. You can reactivate it at any time.",
  "pro.system.dataCatalog.confirm.deactivateTitle": "Deactivate \"{name}\"?",
  "pro.system.dataCatalog.confirm.deleteBody": "This will delete the catalog and all of its key definitions. This cannot be undone.",
  "pro.system.dataCatalog.confirm.deleteTitle": "Permanently delete \"{name}\"?",
  "pro.system.dataCatalog.create": "Create catalog",
  "pro.system.dataCatalog.createModalTitle": "New Data Catalog",
  "pro.system.dataCatalog.creating": "Creating…",
  "pro.system.dataCatalog.emptySubtitle": "Create a catalog to start defining telemetry key mappings.",
  "pro.system.dataCatalog.emptyTitle": "No catalogs yet",
  "pro.system.dataCatalog.enumGroups": "Enum Groups",
  "pro.system.dataCatalog.productModels": "Product Models",
  "pro.system.dataCatalog.enumModal.addValue": "Add value",
  "pro.system.dataCatalog.enumModal.description": "Description",
  "pro.system.dataCatalog.enumModal.descriptionPlaceholder": "Optional additional detail",
  "pro.system.dataCatalog.enumModal.forKey": "For key:",
  "pro.system.dataCatalog.enumModal.labelPlaceholder": "e.g. Battery Over-Voltage",
  "pro.system.dataCatalog.enumModal.labelRequired": "Label *",
  "pro.system.dataCatalog.enumModal.metaJson": "Meta (JSON)",
  "pro.system.dataCatalog.enumModal.metaPlaceholder": "e.g. {\"severity\": \"error\"}",
  "pro.system.dataCatalog.enumModal.rawValue": "Raw value *",
  "pro.system.dataCatalog.enumModal.rawValuePlaceholder": "e.g. A1_5 or 0",
  "pro.system.dataCatalog.enumModal.title": "Add enum value",
  "pro.system.dataCatalog.error.create": "Failed to create catalog",
  "pro.system.dataCatalog.error.load": "Failed to load catalogs",
  "pro.system.dataCatalog.error.loadDetail": "Failed to load catalog",
  "pro.system.dataCatalog.error.metaJson": "Meta must be valid JSON",
  "pro.system.dataCatalog.error.notFound": "Catalog not found.",
  "pro.system.dataCatalog.error.saveEnum": "Failed to save enum value",
  "pro.system.dataCatalog.error.saveKey": "Failed to save key",
  "pro.system.dataCatalog.form.accessLevel": "Access level",
  "pro.system.dataCatalog.form.canonicalUnit": "Canonical unit",
  "pro.system.dataCatalog.form.canonicalUnitPlaceholder": "e.g. W",
  "pro.system.dataCatalog.form.defaultValue": "Default value",
  "pro.system.dataCatalog.form.defaultValuePlaceholder": "e.g. 0",
  "pro.system.dataCatalog.form.description": "Description",
  "pro.system.dataCatalog.form.descriptionPlaceholder": "Optional description",
  "pro.system.dataCatalog.form.displayLabelPlaceholder": "Display Label",
  "pro.system.dataCatalog.form.editable": "Editable",
  "pro.system.dataCatalog.form.enumGroup": "Enum Group",
  "pro.system.dataCatalog.form.labelPlaceholder": "e.g. Grid Power",
  "pro.system.dataCatalog.form.labelRequired": "Label *",
  "pro.system.dataCatalog.form.namePlaceholder": "e.g. Sanctuary 2 v1",
  "pro.system.dataCatalog.form.nameRequired": "Name *",
  "pro.system.dataCatalog.form.newShadowPlaceholder": "Or type a new shadow name…",
  "pro.system.dataCatalog.form.newSuffix": "(new)",
  "pro.system.dataCatalog.form.none": "— None —",
  "pro.system.dataCatalog.form.perKeyValues": "— Per-key values (no group) —",
  "pro.system.dataCatalog.form.precision": "Precision (dp)",
  "pro.system.dataCatalog.form.precisionPlaceholder": "e.g. 2",
  "pro.system.dataCatalog.form.rawKey": "Raw key *",
  "pro.system.dataCatalog.form.rawKeyPlaceholder": "e.g. grid_w",
  "pro.system.dataCatalog.form.rawUnit": "Raw unit",
  "pro.system.dataCatalog.form.rawUnitPlaceholder": "e.g. mW",
  "pro.system.dataCatalog.form.section": "Section",
  "pro.system.dataCatalog.form.sectionPlaceholder": "e.g. Grid",
  "pro.system.dataCatalog.form.sectionPlaceholderGeneral": "General",
  "pro.system.dataCatalog.form.settingsOptions": "Settings options",
  "pro.system.dataCatalog.form.shadowName": "Shadow name",
  "pro.system.dataCatalog.form.sortOrder": "Sort order",
  "pro.system.dataCatalog.form.valueType": "Value type",
  "pro.system.dataCatalog.keys.col.access": "Access",
  "pro.system.dataCatalog.keys.col.default": "Default",
  "pro.system.dataCatalog.keys.col.key": "Key",
  "pro.system.dataCatalog.keys.col.label": "Label",
  "pro.system.dataCatalog.keys.col.section": "Section",
  "pro.system.dataCatalog.keys.col.type": "Type",
  "pro.system.dataCatalog.keys.col.unit": "Unit",
  "pro.system.dataCatalog.keys.search": "Search keys…",
  "pro.system.dataCatalog.modal.addDatapoint": "Add datapoint",
  "pro.system.dataCatalog.modal.addSetting": "Add setting",
  "pro.system.dataCatalog.modal.editDatapoint": "Edit datapoint",
  "pro.system.dataCatalog.modal.editSetting": "Edit setting",
  "pro.system.dataCatalog.newCatalog": "New catalog",
  "pro.system.dataCatalog.realtime.definitionsTitle": "Real-time definitions",
  "pro.system.dataCatalog.realtime.emptySubtitle": "Click 'Add datapoint' to define a telemetry key.",
  "pro.system.dataCatalog.realtime.emptySubtitleStart": "Add a real-time key to get started",
  "pro.system.dataCatalog.realtime.emptyTitle": "No real-time keys",
  "pro.system.dataCatalog.saveDatapoint": "Save datapoint",
  "pro.system.dataCatalog.saveSetting": "Save setting",
  "pro.system.dataCatalog.saving": "Saving…",
  "pro.system.dataCatalog.searchPlaceholder": "Search catalogs…",
  "pro.system.dataCatalog.section.realtime": "Real-time",
  "pro.system.dataCatalog.section.settings": "Settings",
  "pro.system.dataCatalog.settings.definitionsTitle": "Settings definitions",
  "pro.system.dataCatalog.settings.emptySubtitle": "Click 'Add setting' to define a shadow settings key.",
  "pro.system.dataCatalog.settings.emptySubtitleStart": "Add a settings key to get started",
  "pro.system.dataCatalog.settings.emptyTitle": "No settings keys",
  "pro.system.dataCatalog.subtitle": "Manage telemetry and shadow settings key definitions per product model.",
  "pro.system.dataCatalog.title": "Data Catalog",
  "pro.system.dataCatalog.valueType.bitmask": "bitmask",
  "pro.system.dataCatalog.valueType.boolean": "boolean",
  "pro.system.dataCatalog.valueType.enum": "enum",
  "pro.system.dataCatalog.valueType.number": "number",
  "pro.system.dataCatalog.valueType.string": "string",
  "pro.system.enums.addCode": "Add code",
  "pro.system.enums.badge.inactive": "Inactive",
  "pro.system.enums.codeCount": "{count, plural, one {# code} other {# codes}}",
  "pro.system.enums.codesTotal": "{count, plural, one {# code total} other {# codes total}}",
  "pro.system.enums.col.key": "Key",
  "pro.system.enums.col.label": "Label",
  "pro.system.enums.col.severity": "Severity",
  "pro.system.enums.confirm.deleteBody": "\"{label}\" will be permanently removed from this group.",
  "pro.system.enums.confirm.deleteTitle": "Delete \"{key}\"?",
  "pro.system.enums.create.create": "Create group",
  "pro.system.enums.create.creating": "Creating…",
  "pro.system.enums.create.description": "Description",
  "pro.system.enums.create.descriptionPlaceholder": "Optional description",
  "pro.system.enums.create.namePlaceholder": "e.g. Alarm Codes",
  "pro.system.enums.create.nameRequired": "Name *",
  "pro.system.enums.create.title": "New Enum Group",
  "pro.system.enums.empty": "No enum groups yet. Create one to start building a shared code library.",
  "pro.system.enums.emptyCodes": "No codes yet. Add the first one.",
  "pro.system.enums.emptyFiltered": "No codes match your filter.",
  "pro.system.enums.error.create": "Failed to create group",
  "pro.system.enums.error.delete": "Failed to delete",
  "pro.system.enums.error.load": "Failed to load enum groups",
  "pro.system.enums.error.loadDetail": "Failed to load group",
  "pro.system.enums.error.metaJson": "Meta must be valid JSON",
  "pro.system.enums.error.notFound": "Group not found",
  "pro.system.enums.error.saveMeta": "Failed to save",
  "pro.system.enums.error.saveValue": "Failed to save value",
  "pro.system.enums.filterCount": "{filtered} of {total}",
  "pro.system.enums.filterPlaceholder": "Filter by code or label…",
  "pro.system.enums.form.descriptionPlaceholder": "Optional description",
  "pro.system.enums.newGroup": "New group",
  "pro.system.enums.noDescription": "No description",
  "pro.system.enums.saving": "Saving…",
  "pro.system.enums.subtitle": "Shared code libraries for alarm, fault, and status values. Link a group to a catalog key to manage codes in one place.",
  "pro.system.enums.title": "Enum Groups",
  "pro.system.enums.valueModal.add": "Add Value",
  "pro.system.enums.valueModal.description": "Description",
  "pro.system.enums.valueModal.descriptionPlaceholder": "Optional longer description",
  "pro.system.enums.valueModal.edit": "Edit Value",
  "pro.system.enums.valueModal.key": "Key",
  "pro.system.enums.valueModal.keyPlaceholder": "e.g. A1_5 or 0",
  "pro.system.enums.valueModal.keyRequired": "Key *",
  "pro.system.enums.valueModal.labelPlaceholder": "e.g. Battery Over-Voltage",
  "pro.system.enums.valueModal.labelRequired": "Label *",
  "pro.system.enums.valueModal.metaJson": "Meta (JSON)",
  "pro.system.enums.valueModal.metaPlaceholder": "e.g. {\"severity\": \"error\", \"alertLevel\": \"Customer\"}",
  "pro.system.events.active": "Active",
  "pro.system.events.cancel": "Cancel",
  "pro.system.events.col.domain": "Domain",
  "pro.system.events.col.enabled": "Enabled",
  "pro.system.events.col.historyType": "History type",
  "pro.system.events.col.key": "Key",
  "pro.system.events.col.label": "Label",
  "pro.system.events.col.shouldLog": "Should log",
  "pro.system.events.col.source": "Source",
  "pro.system.events.col.status": "Status",
  "pro.system.events.disabled": "Disabled",
  "pro.system.events.domain.apiKey": "API key",
  "pro.system.events.domain.certification": "Certification",
  "pro.system.events.domain.localization": "Localization",
  "pro.system.events.domain.organization": "Organization",
  "pro.system.events.domain.partnerApi": "Partner API",
  "pro.system.events.domain.permission": "Permission",
  "pro.system.events.domain.product": "Product",
  "pro.system.events.domain.role": "Role",
  "pro.system.events.domain.user": "User",
  "pro.system.events.emptySubtitle": "No system events match your filters.",
  "pro.system.events.emptyTitle": "No events",
  "pro.system.events.enabled": "Enabled",
  "pro.system.events.error.labelRequired": "Label is required",
  "pro.system.events.error.load": "Failed to load system events",
  "pro.system.events.error.loadEvent": "Failed to load event",
  "pro.system.events.error.save": "Failed to save event",
  "pro.system.events.form.description": "Description",
  "pro.system.events.form.descriptionPlaceholder": "Optional description",
  "pro.system.events.form.enabled": "Enabled",
  "pro.system.events.form.enabledHelp": "When disabled, history and notifications are suppressed.",
  "pro.system.events.form.key": "Key",
  "pro.system.events.form.labelPlaceholder": "Display label",
  "pro.system.events.form.labelRequired": "Label",
  "pro.system.events.form.shouldLog": "Should log",
  "pro.system.events.form.shouldLogHelp": "When off, emitters skip writing history for this event.",
  "pro.system.events.hideRetired": "Hide retired",
  "pro.system.events.loading": "Loading…",
  "pro.system.events.modal.edit": "Edit event",
  "pro.system.events.no": "No",
  "pro.system.events.retired": "Retired",
  "pro.system.events.retiredNotice": "This event is retired and no longer emitted.",
  "pro.system.events.saveChanges": "Save changes",
  "pro.system.events.saving": "Saving…",
  "pro.system.events.search": "Search events…",
  "pro.system.events.showRetired": "Show retired",
  "pro.system.events.signInRequired": "Sign in required to manage system events.",
  "pro.system.events.source.productService": "Product service",
  "pro.system.events.source.userService": "User service",
  "pro.system.events.title": "System events",
  "pro.system.events.yes": "Yes",
  "pro.system.featureFlags.add": "Add",
  "pro.system.featureFlags.aria.searchResults": "Search results",
  "pro.system.featureFlags.col.globalValue": "Global value",
  "pro.system.featureFlags.col.key": "Key",
  "pro.system.featureFlags.col.label": "Label",
  "pro.system.featureFlags.col.overrides": "Overrides",
  "pro.system.featureFlags.col.group": "Group",
  "pro.system.featureFlags.group.all": "All ({count})",
  "pro.system.featureFlags.group.ungrouped": "Ungrouped",
  "pro.system.featureFlags.group.tabsAria": "Feature groups",
  "pro.system.featureFlags.scope.globalBadge": "Global only",
  "pro.system.featureFlags.col.type": "Type",
  "pro.system.featureFlags.deleting": "Deleting…",
  "pro.system.featureFlags.disabled": "Disabled",
  "pro.system.featureFlags.emptySubtitle": "Create your first flag to get started.",
  "pro.system.featureFlags.emptyTitle": "No feature flags yet",
  "pro.system.featureFlags.enabled": "Enabled",
  "pro.system.featureFlags.error.addOverride": "Failed to add override",
  "pro.system.featureFlags.error.delete": "Failed to delete feature flag",
  "pro.system.featureFlags.error.load": "Failed to load feature flags",
  "pro.system.featureFlags.error.loadFlag": "Failed to load feature flag",
  "pro.system.featureFlags.error.loadOverrides": "Failed to load overrides",
  "pro.system.featureFlags.error.removeOverride": "Failed to remove override",
  "pro.system.featureFlags.error.save": "Failed to save feature flag",
  "pro.system.featureFlags.form.description": "Description",
  "pro.system.featureFlags.form.descriptionPlaceholder": "Optional description",
  "pro.system.featureFlags.form.globalValue": "Global value *",
  "pro.system.featureFlags.form.keyPlaceholder": "e.g. pro_web.dark_mode_toggle",
  "pro.system.featureFlags.form.keyRequired": "Key *",
  "pro.system.featureFlags.form.labelPlaceholder": "e.g. Dark Mode Toggle",
  "pro.system.featureFlags.form.labelRequired": "Label *",
  "pro.system.featureFlags.form.options": "Options *",
  "pro.system.featureFlags.form.typeRequired": "Type *",
  "pro.system.featureFlags.modal.create": "New feature flag",
  "pro.system.featureFlags.modal.edit": "Edit feature flag",
  "pro.system.featureFlags.noOptions": "— No options defined —",
  "pro.system.featureFlags.optionPlaceholder": "Add an option value",
  "pro.system.featureFlags.overrideCount": "{count, plural, one {# override} other {# overrides}}",
  "pro.system.featureFlags.overrides.add": "Add override",
  "pro.system.featureFlags.overrides.empty": "No per-user overrides yet.",
  "pro.system.featureFlags.overrides.searchUsers": "Search users by name or email…",
  "pro.system.featureFlags.overrides.title": "Overrides",
  "pro.system.featureFlags.overrides.valueLabel": "· value:",
  "pro.system.featureFlags.overrides.none": "No user or organization overrides yet.",
  "pro.system.featureFlags.overrides.organizationLabel": "Organization",
  "pro.system.featureFlags.overrides.targetLabel": "Override target",
  "pro.system.featureFlags.overrides.targetUsers": "Users",
  "pro.system.featureFlags.overrides.targetOrganizations": "Organizations",
  "pro.system.featureFlags.overrides.searchOrganizations": "Search organizations by name…",
  "pro.system.featureFlags.form.orgConflictStrategy": "When a user's organizations disagree",
  "pro.system.featureFlags.orgConflictStrategy.mostPermissive": "Most permissive wins (any org enabling it)",
  "pro.system.featureFlags.orgConflictStrategy.mostRestrictive": "Most restrictive wins (any org disabling it)",
  "pro.system.featureFlags.orgConflictStrategy.primaryOrg": "User's primary organization wins",
  "pro.system.featureFlags.form.orgAdminAccess": "Organization admin access",
  "pro.system.featureFlags.form.group": "Group",
  "pro.system.featureFlags.form.groupPlaceholder": "e.g. Certification or AI › Insights",
  "pro.system.featureFlags.form.scope": "Overrides",
  "pro.system.featureFlags.scope.overridable": "Users and organizations can override",
  "pro.system.featureFlags.scope.global": "Global only (read by other services)",
  "pro.system.featureFlags.orgAdminAccess.hidden": "Hidden from org admins",
  "pro.system.featureFlags.orgAdminAccess.readOnly": "Visible to org admins, managed by Lion Energy",
  "pro.system.featureFlags.orgAdminAccess.editable": "Org admins can override for their organization",
  "pro.organizations.featureOverrides.add": "Add feature override",
  "pro.organizations.featureOverrides.selectFlag": "— Select a feature —",
  "pro.organizations.featureOverrides.remove": "Remove override",
  "pro.organizations.featureOverrides.removing": "Removing…",
  "pro.organizations.featureOverrides.managed": "Managed by Lion Energy",
  "pro.organizations.featureOverrides.overrideValue": "override:",
  "pro.organizations.featureOverrides.defaultValue": "default:",
  "pro.organizations.featureOverrides.empty": "{name} has no active feature overrides.",
  "pro.organizations.featureOverrides.error.load": "Failed to load feature overrides",
  "pro.system.featureFlags.removeOption": "Remove {opt}",
  "pro.system.featureFlags.saveChanges": "Save changes",
  "pro.system.featureFlags.saving": "Saving…",
  "pro.system.featureFlags.search": "Search flags by key or label…",
  "pro.system.featureFlags.signInRequired": "Sign in required to view feature flags.",
  "pro.system.featureFlags.title": "Features",
  "pro.system.featureFlags.type.boolean": "Boolean",
  "pro.system.featureFlags.type.enum": "Enum",
  "pro.system.featureFlags.type.number": "Number",
  "pro.system.featureFlags.type.string": "String",
  "pro.system.featureFlags.valueLabel": "Flag value",
  "pro.system.localization.aiFill.apply": "Start",
  "pro.system.localization.aiFill.error.generic": "AI fill failed.",
  "pro.system.localization.aiFill.error.providerNotConfigured": "AI translation isn't enabled yet — ask an admin to configure it.",
  "pro.system.localization.aiFill.intro": "AI will translate {count, plural, one {# empty field} other {# empty fields}} from {source} to {target}.",
  "pro.system.localization.aiFill.options.summaryLine": "{count, plural, one {# field} other {# fields}} in {context}",
  "pro.system.localization.aiFill.progress": "Translating {context}… {current} of {total}",
  "pro.system.localization.aiFill.result.partialFailure": "Some contexts could not be translated:",
  "pro.system.localization.aiFill.result.summary": "Filled {filled} of {total} fields",
  "pro.system.localization.aiFill.title": "Fill empty with AI",
  "pro.system.localization.cannotDeleteDefault": "The default English language cannot be deleted",
  "pro.system.localization.col.code": "Code",
  "pro.system.localization.col.description": "Description",
  "pro.system.localization.col.flag": "Flag",
  "pro.system.localization.col.name": "Name",
  "pro.system.localization.col.status": "Status",
  "pro.system.localization.confirm.delete": "Delete",
  "pro.system.localization.confirm.deleteBody": "Delete \"{name}\" ({code})? This removes its translations and returns any users on this language to English.",
  "pro.system.localization.confirm.deleteTitle": "Delete language",
  "pro.system.localization.definitions.addMissing": "Add {count, plural, one {# missing key} other {# missing keys}} to draft",
  "pro.system.localization.definitions.addString": "Add string",
  "pro.system.localization.definitions.addUntranslated": "Add {count, plural, one {# untranslated key} other {# untranslated keys}} to draft",
  "pro.system.localization.definitions.aiFill": "Fill empty with AI",
  "pro.system.localization.definitions.aiFillNeedsDefault": "Empty fields need a default-language value to translate from",
  "pro.system.localization.definitions.aria.collapse": "Collapse section",
  "pro.system.localization.definitions.aria.expand": "Expand section",
  "pro.system.localization.definitions.aria.languages": "Languages",
  "pro.system.localization.definitions.confirm.leave": "Leave",
  "pro.system.localization.definitions.confirm.leaveBody": "You have unsaved definition changes. Leave without saving?",
  "pro.system.localization.definitions.confirm.leaveTitle": "Unsaved changes",
  "pro.system.localization.definitions.confirm.removeOrphans": "Remove",
  "pro.system.localization.definitions.confirm.removeOrphansBody": "Delete {count, plural, one {# orphaned certification.content key} other {# orphaned certification.content keys}} from all languages? These keys are not referenced by any training or quiz field.",
  "pro.system.localization.definitions.confirm.removeOrphansTitle": "Remove orphaned keys",
  "pro.system.localization.definitions.emptyKey": "(empty key)",
  "pro.system.localization.definitions.error.loadLanguages": "Failed to load languages",
  "pro.system.localization.definitions.error.loadModule": "Failed to load module",
  "pro.system.localization.definitions.error.removeOrphans": "Failed to remove orphaned keys",
  "pro.system.localization.definitions.error.save": "Failed to save definitions",
  "pro.system.localization.definitions.loadingLanguages": "Loading languages…",
  "pro.system.localization.definitions.moduleEmpty": "No strings in this module yet.",
  "pro.system.localization.definitions.removeOrphans": "Remove {count, plural, one {# orphaned key} other {# orphaned keys}}",
  "pro.system.localization.definitions.resetAll": "Reset all unsaved changes",
  "pro.system.localization.definitions.resetSection": "Reset unsaved changes in this section",
  "pro.system.localization.definitions.row.collapseMultiline": "Collapse to one line",
  "pro.system.localization.definitions.row.default": "Default",
  "pro.system.localization.definitions.row.discardNew": "Discard new string",
  "pro.system.localization.definitions.row.expandMultiline": "Expand multiline editor",
  "pro.system.localization.definitions.row.keyPlaceholder": "key.path",
  "pro.system.localization.definitions.row.messageText": "Message text",
  "pro.system.localization.definitions.row.remove": "Remove",
  "pro.system.localization.definitions.row.resetSaved": "Reset to saved value",
  "pro.system.localization.definitions.row.undoDelete": "Undo delete",
  "pro.system.localization.definitions.save": "Save {count, plural, one {# change} other {# changes}}",
  "pro.system.localization.definitions.saving": "Saving…",
  "pro.system.localization.definitions.section.meta": "v{version} · {count, plural, one {# string} other {# strings}}",
  "pro.system.localization.definitions.status.synced": "Synced",
  "pro.system.localization.definitions.status.unsaved": "Unsaved",
  "pro.system.localization.definitions.status.unsavedCreate": "Unsaved - Flagged for creation",
  "pro.system.localization.definitions.status.unsavedDelete": "Unsaved - Flagged for deletion",
  "pro.system.localization.definitions.unsavedBadge": "{count} unsaved",
  "pro.system.localization.definitions.validation.duplicateKey": "Duplicate keys in {lang}/{ctx}: {keys}",
  "pro.system.localization.definitions.validation.emptyKey": "Empty key in {lang}/{ctx} — fill or remove the row",
  "pro.system.localization.error.deleteLanguage": "Failed to delete language",
  "pro.system.localization.error.languageNotFound": "Language not found.",
  "pro.system.localization.error.loadLanguage": "Failed to load language",
  "pro.system.localization.error.loadLanguages": "Failed to load languages",
  "pro.system.localization.error.saveLanguage": "Failed to save language",
  "pro.system.localization.form.code": "Code",
  "pro.system.localization.form.codeRequired": "Code *",
  "pro.system.localization.form.createTitle": "Create language",
  "pro.system.localization.form.description": "Description",
  "pro.system.localization.form.descriptionPlaceholder": "Optional description",
  "pro.system.localization.form.editDefinitionsLink": "Edit definitions for {name}",
  "pro.system.localization.form.editTitle": "Edit language",
  "pro.system.localization.form.enabled": "Enabled",
  "pro.system.localization.form.flag": "Flag",
  "pro.system.localization.form.flagPlaceholder": "Click or drag a flag image here",
  "pro.system.localization.form.flagSignIn": "Sign in required to upload a flag.",
  "pro.system.localization.form.isDefault": "Default language",
  "pro.system.localization.form.isDefaultHint": "Used when a user has no preference. English remains the translation fallback.",
  "pro.system.localization.form.namePlaceholder": "English",
  "pro.system.localization.form.nameRequired": "Name *",
  "pro.system.localization.form.saving": "Saving…",
  "pro.system.localization.gaps.addMissing": "Add missing keys to draft",
  "pro.system.localization.gaps.col.key": "Key",
  "pro.system.localization.gaps.col.location": "Location",
  "pro.system.localization.gaps.col.snippet": "Snippet",
  "pro.system.localization.gaps.error.array": "Report must be an array or '{' gaps: [] '}'",
  "pro.system.localization.gaps.error.invalid": "Invalid report file",
  "pro.system.localization.gaps.instructions": "Run pnpm i18n:extract locally, then upload the JSON report to review hardcoded strings and add English keys to this module draft.",
  "pro.system.localization.gaps.modalInstructions": "Run pnpm i18n:extract, then upload the JSON report to review hardcoded strings for {languageCode} / {context} and add missing English keys to the draft.",
  "pro.system.localization.gaps.summary": "{findings, plural, one {# finding} other {# findings}} · {missing} not yet in definitions",
  "pro.system.localization.health.bar.aria": "Translated {translated}, untranslated {untranslated}, unlocalized {unlocalized}, orphaned {orphaned}, of {total}",
  "pro.system.localization.health.bar.orphaned": "Orphaned content keys: {count}",
  "pro.system.localization.health.bar.translated": "Translated (all languages): {count}",
  "pro.system.localization.health.bar.unlocalized": "Unlocalized: {count}",
  "pro.system.localization.health.bar.untranslated": "Untranslated (missing in some languages): {count}",
  "pro.system.localization.health.catalogAria": "Catalog — Click to view catalog JSON",
  "pro.system.localization.health.catalogBadge": "Catalog",
  "pro.system.localization.health.catalogClickHint": "Click to view catalog JSON",
  "pro.system.localization.health.catalogGenerated": "Generated: {date}",
  "pro.system.localization.health.catalogModalDescription": "Build-time scan of t() keys and heuristic unlocalized UI strings. Translation coverage also includes keys defined on the default language in the database.",
  "pro.system.localization.health.catalogModalTitle": "Localization catalog",
  "pro.system.localization.health.catalogSource": "Source: {source}",
  "pro.system.localization.health.catalogTooltipTitle": "Localization catalog",
  "pro.system.localization.health.copied": "Copied!",
  "pro.system.localization.health.copyJson": "Copy JSON",
  "pro.system.localization.health.error.load": "Failed to load localization health",
  "pro.system.localization.health.error.loadCatalog": "Failed to load catalog",
  "pro.system.localization.health.legend.localizedKeys": "Localized keys {count}",
  "pro.system.localization.health.legend.orphaned": "Orphaned {count}",
  "pro.system.localization.health.legend.total": "Total {count}",
  "pro.system.localization.health.legend.translated": "Translated {count}",
  "pro.system.localization.health.legend.unlocalized": "Unlocalized {count}",
  "pro.system.localization.health.legend.untranslated": "Untranslated {count}",
  "pro.system.localization.health.noCatalog": "No string catalog available",
  "pro.system.localization.health.noCatalogHint": "Run pnpm i18n:extract in pro-web (or deploy a build) to publish the string catalog used for this report.",
  "pro.system.localization.health.openDefinitions": "Open {name} definitions",
  "pro.system.localization.health.title": "Localization Health",
  "pro.system.localization.searchPlaceholder": "Search languages…",
  "pro.system.localization.signInRequired": "Sign in required.",
  "pro.system.localization.status.disabled": "Disabled",
  "pro.system.localization.status.enabled": "Enabled",
  "pro.system.mockData.clear": "Clear",
  "pro.system.mockData.confirm.clear": "Delete permanently",
  "pro.system.mockData.confirm.clearBody": "This will permanently remove all {count} mocked {type}. This cannot be undone.",
  "pro.system.mockData.confirm.clearTitle": "Delete all {count} mocked {type}?",
  "pro.system.mockData.count": "Count",
  "pro.system.mockData.done": "Done",
  "pro.system.mockData.error.generateOrgs": "Failed to generate organizations.",
  "pro.system.mockData.error.generateProducts": "Failed to generate products.",
  "pro.system.mockData.error.generateUsers": "Failed to generate users.",
  "pro.system.mockData.error.registerFailed": "Register failed ({status})",
  "pro.system.mockData.error.selectOrg": "Select a target organization first.",
  "pro.system.mockData.mockedCount": "{count} mocked",
  "pro.system.mockData.orgs.description": "Creates organizations with random names and US city/state locations.",
  "pro.system.mockData.orgs.generate": "Generate organizations",
  "pro.system.mockData.orgs.title": "Organizations",
  "pro.system.mockData.products.description": "Creates products assigned to the selected organization with random models and locations.",
  "pro.system.mockData.products.generate": "Generate products",
  "pro.system.mockData.products.targetOrg": "Target organization *",
  "pro.system.mockData.products.title": "Products",
  "pro.system.mockData.results.col.detail": "Detail",
  "pro.system.mockData.results.col.id": "ID",
  "pro.system.mockData.results.col.name": "Name",
  "pro.system.mockData.subtitle": "Generate bulk test data. Max 50 records per run. Super-admin only.",
  "pro.system.mockData.title": "Mock Data",
  "pro.system.mockData.type.organizations": "organizations",
  "pro.system.mockData.type.products": "products",
  "pro.system.mockData.type.users": "users",
  "pro.system.mockData.users.generate": "Generate users",
  "pro.system.mockData.users.loadingOrgs": "Loading organizations…",
  "pro.system.mockData.users.noRole": "— No role —",
  "pro.system.mockData.users.passwordNote": "Registers users with random names and emails. Password: Password123",
  "pro.system.mockData.users.role": "Role (optional)",
  "pro.system.mockData.users.selectOrg": "Select an organization",
  "pro.system.mockData.users.targetOrg": "Target organization (optional)",
  "pro.system.mockData.users.title": "Users",
  "pro.system.modules.badge.inactive": "Inactive",
  "pro.system.modules.confirm.activate": "Activate",
  "pro.system.modules.confirm.activateBody": "Once you save this change, the course will be displayed to installers in the Certification Portal immediately.",
  "pro.system.modules.confirm.activateTitle": "Activate this course?",
  "pro.system.modules.confirm.deactivate": "Deactivate",
  "pro.system.modules.confirm.deactivateBody": "Once you save this change, the course will be hidden from installers in the Certification Portal immediately.",
  "pro.system.modules.confirm.deactivateTitle": "Deactivate this course?",
  "pro.system.modules.confirm.deleteTrainingBody": "Delete \"{title}\"? This cannot be undone.",
  "pro.system.modules.confirm.deleteTrainingTitle": "Delete training",
  "pro.system.modules.confirm.thisTraining": "this training",
  "pro.system.modules.course.cardPreviewLabel": "Home card",
  "pro.system.modules.course.certificatePreviewLabel": "Certificate",
  "pro.system.modules.course.heroPreviewLabel": "Course page hero",
  "pro.system.modules.course.lessonCardPreviewLabel": "Lesson card",
  "pro.system.modules.course.pagePreviewLabel": "Course page",
  "pro.system.modules.course.previewLabel": "Portal preview",
  "pro.system.modules.course.previewsLabel": "Previews:",
  "pro.system.modules.course.progressPreviewLabel": "Profile progress card",
  "pro.system.modules.course.subtitle": "Course card shown on the certification portal",
  "pro.system.modules.course.title": "Course",
  "pro.system.modules.course.trainingsLabel": "Trainings",
  "pro.system.modules.error.load": "Failed to load module",
  "pro.system.modules.error.notFound": "Module not found.",
  "pro.system.modules.error.save": "Failed to save module.",
  "pro.system.modules.error.uploadHero": "Failed to upload hero image.",
  "pro.system.modules.form.active": "Active (visible to technicians)",
  "pro.system.modules.form.addModule": "Add module",
  "pro.system.modules.form.certificateCourseText": "Certificate Course Text",
  "pro.system.modules.form.certificateCourseTextPlaceholder":
    "e.g. Sanctuary 3 Installer Training",
  "pro.system.modules.form.certificateHeaderText": "Certificate Header Text",
  "pro.system.modules.form.certificateHeaderTextPlaceholder":
    "e.g. Certificate of Completion",
  "pro.system.modules.form.createTitle": "Add Module",
  "pro.system.modules.form.description": "Description",
  "pro.system.modules.form.descriptionPlaceholder": "Optional module description",
  "pro.system.modules.form.editTitle": "Module Settings",
  "pro.system.modules.form.heroImage": "Hero image",
  "pro.system.modules.form.heroPlaceholder": "Click or drag a hero image here",
  "pro.system.modules.form.saveChanges": "Save changes",
  "pro.system.modules.form.slug": "Slug",
  "pro.system.modules.form.slugPlaceholder": "e.g. ems-c-installation",
  "pro.system.modules.form.slugReadonlyHint": "Set when the module is created; used for localization keys.",
  "pro.system.modules.form.slugRequired": "Slug *",
  "pro.system.modules.form.title": "Title",
  "pro.system.modules.form.titlePlaceholder": "e.g. EMS-C Installation",
  "pro.system.modules.form.titleRequired": "Title *",
  "pro.system.modules.inactiveBanner": "This certification module is inactive",
  "pro.system.modules.settings": "Module Settings",
  "pro.system.modules.training.col.section": "Section",
  "pro.system.modules.training.col.step": "Step",
  "pro.system.modules.training.col.title": "Title",
  "pro.system.modules.training.col.video": "Video",
  "pro.system.modules.training.no": "No",
  "pro.system.modules.training.yes": "Yes",
  "pro.system.modules.trainings.add": "Add training",
  "pro.system.modules.trainings.emptySubtitle": "No trainings have been added to this module yet.",
  "pro.system.modules.trainings.emptyTitle": "No trainings",
  "pro.system.modules.trainings.title": "Trainings",
  "pro.system.modules.validation.slugRequired": "Slug is required.",
  "pro.system.modules.validation.titleRequired": "Title is required.",
  "pro.system.simulation.active.empty": "No products are currently simulating.",
  "pro.system.simulation.active.running": "{count} running",
  "pro.system.simulation.active.title": "Active Sessions",
  "pro.system.simulation.clearHistory": "Clear Historical Data",
  "pro.system.simulation.col.expires": "Expires",
  "pro.system.simulation.col.product": "Product",
  "pro.system.simulation.col.sessionStarted": "Session started",
  "pro.system.simulation.confirm.clear": "Clear history",
  "pro.system.simulation.confirm.clearBody": "This will permanently delete all telemetry history rows for this product. The simulation state is preserved.",
  "pro.system.simulation.confirm.clearTitle": "Clear history for \"{name}\"?",
  "pro.system.simulation.error.load": "Failed to load simulation stats.",
  "pro.system.simulation.paused.count": "{count} paused",
  "pro.system.simulation.paused.empty": "No paused simulations.",
  "pro.system.simulation.paused.title": "Paused Simulations",
  "pro.system.simulation.refresh": "Refresh",
  "pro.system.simulation.stat.active": "Actively simulating",
  "pro.system.simulation.stat.historyRows": "History rows",
  "pro.system.simulation.stat.retention": "Retention window",
  "pro.system.simulation.stat.total": "Total simulated products",
  "pro.system.simulation.subtitle": "Monitor and manage physics-driven product simulations. Super-admin only.",
  "pro.system.simulation.title": "Product Simulation",
  "pro.users.accountDisabled": "Account disabled",
  "pro.users.accountStatus": "Account status",
  "pro.users.addMember": "Add member",
  "pro.users.addOverride": "Add override",
  "pro.users.certifications": "Certifications",
  "pro.users.contact": "Contact",
  "pro.users.editUser": "Edit user",
  "pro.users.email": "Email",
  "pro.users.emptySubtitle": "Try adjusting your filters or search query.",
  "pro.users.featureOverrides": "Feature Overrides",
  "pro.users.featureOverridesSubtitle": "Feature flag values overridden for this user.",
  "pro.users.firstName": "First name",
  "pro.users.globalPermissions": "Global permissions",
  "pro.users.globalRoles": "Global Roles",
  "pro.users.identity": "Identity",
  "pro.users.lastName": "Last name",
  "pro.users.loadingUser": "Loading user…",
  "pro.users.membershipRequested": "Membership requested",
  "pro.users.noGlobalPermissions": "No global permissions assigned.",
  "pro.users.noGlobalRoles": "No global roles are configured.",
  "pro.users.organization": "Organization",
  "pro.users.organizations": "Organizations",
  "pro.users.permissions": "Permissions",
  "pro.users.phone": "Phone",
  "pro.users.role": "Role",
  "pro.users.roles": "Roles",
  "pro.users.security": "Security",
  "pro.users.selectOrganizationFirst": "Select an organization first",
  "pro.users.signInRequired": "Sign in required to view users.",
  "pro.users.statusActive": "Active",
  "pro.users.statusInvited": "Invited",
  "pro.users.statusPending": "Pending",
  "pro.users.subscriptions": "Subscriptions",
  "pro.users.subscriptions.edit": "Edit subscriptions",
  "pro.users.subscriptions.error.load": "Failed to load user subscriptions",
  "pro.users.subscriptions.event": "event",
  "pro.users.subscriptions.events": "events",
  "pro.users.subscriptions.noSubscriptions": "No subscriptions found",
  "pro.users.subscriptions.productPrefix": "Product ",
  "pro.users.unableToLoadPermissions": "Unable to load permissions.",
  "pro.users.unsavedChanges": "Unsaved changes",
  "pro.users.viewUser": "View user",
  "shared.chart.timeSeries": "Time series chart",
  "shared.colorPicker.chooseColor": "Choose color",
  "shared.confirm.typeToConfirm": "Type \"{phrase}\" to confirm",
  "shared.dashboard.addWidget": "Add Widget",
  "shared.dashboard.deleteWidget": "Delete widget",
  "shared.dashboard.done": "Done",
  "shared.dashboard.doneEditing": "Done editing dashboard",
  "shared.dashboard.editDashboard": "Edit dashboard",
  "shared.dashboard.editWidget": "Edit Widget",
  "shared.dashboard.enterFullscreen": "Enter fullscreen",
  "shared.dashboard.entity": "Entity",
  "shared.dashboard.entityProducts": "Products",
  "shared.dashboard.entityUsers": "Users",
  "shared.dashboard.exitFullscreen": "Exit Fullscreen",
  "shared.dashboard.fullscreen": "Fullscreen",
  "shared.dashboard.title": "Title",
  "shared.dashboard.type": "Type",
  "shared.dashboard.widgetSettings": "Widget settings",
  "shared.dashboard.widgetTitle": "Widget title",
  "shared.dashboard.widgetTypeAlerts": "Alerts",
  "shared.dashboard.widgetTypeMap": "Map",
  "shared.dashboard.widgetTypeMicroGrid": "MicroGrid",
  "shared.dashboard.widgetTypeTable": "Table",
  "shared.filter.all": "All",
  "shared.filter.clear": "Clear",
  "shared.filter.clearSearch": "Clear search",
  "shared.filter.columns": "Columns",
  "shared.filter.configure": "Configure",
  "shared.filter.configureFilters": "Configure filters",
  "shared.filter.filters": "Filters",
  "shared.filter.resetFilters": "Reset filters",
  "shared.filter.search": "Search",
  "shared.form.countryCode": "Country code",
  "shared.form.phoneNumber": "Phone number",
  "shared.languageToggle.changeLanguage": "Change language",
  "shared.languageToggle.changeLanguageNamed": "Change language ({name})",
  "shared.listView.active": "Active",
  "shared.listView.bulkActions": "Bulk actions",
  "shared.listView.cancelMembershipRequest": "Cancel membership request",
  "shared.listView.cardsPerPage": "Cards per page",
  "shared.listView.clearSelection": "Clear selection",
  "shared.listView.disabled": "Disabled",
  "shared.listView.endDateTime": "End date and time",
  "shared.listView.invited": "Invited",
  "shared.listView.invitedToJoin": "You have been invited to join",
  "shared.listView.locked": "Locked",
  "shared.listView.moreOptions": "More options",
  "shared.listView.next": "Next",
  "shared.listView.nextCta": "Next CTA",
  "shared.listView.notEnrolledModules": "Not enrolled in any modules",
  "shared.listView.pending": "Pending",
  "shared.listView.pendingApproval": "Pending approval",
  "shared.listView.pendingRequest": "Pending request",
  "shared.listView.previous": "Previous",
  "shared.listView.simulated": "Simulated",
  "shared.listView.startDateTime": "Start date and time",
  "shared.listView.view": "View",
  "shared.listView.viewOrganization": "View organization",
  "shared.listView.viewUser": "View user",
  "shared.localizedField.default": "Default",
  "shared.map.enableScrollZoom": "Click to enable scroll zoom",
  "shared.media.dropImage": "Click or drag an image here",
  "shared.media.dropVideo": "Click or drag a video here",
  "shared.media.previewVideo": "Preview video",
  "shared.media.remove": "Remove",
  "shared.media.removeItem": "Remove item",
  "shared.media.selectItem": "Select item",
  "shared.media.upload": "Upload",
  "shared.media.uploading": "Uploading",
  "shared.media.youtubeTrainingVideo": "YouTube training video",
  "shared.media.youtubeVideo": "YouTube video",
  "shared.microgrid.acceptIdChange": "Accept ID change",
  "shared.microgrid.acceptTitleChange": "Accept title change",
  "shared.microgrid.add": "Add",
  "shared.microgrid.addAlert": "Add Alert",
  "shared.microgrid.addDataPoint": "Add Data Point",
  "shared.microgrid.addHighlight": "Add Highlight",
  "shared.microgrid.addImage": "Add Image",
  "shared.microgrid.addLabel": "Add Label",
  "shared.microgrid.addMapping": "Add Mapping",
  "shared.microgrid.addNode": "Add Node",
  "shared.microgrid.addPort": "Add Port",
  "shared.microgrid.alertActionRequired": "ALERT - ACTION REQUIRED",
  "shared.microgrid.alertMessage": "Alert message",
  "shared.microgrid.alertName": "Alert name",
  "shared.microgrid.alerts": "Alerts",
  "shared.microgrid.alignBottom": "Align Bottom",
  "shared.microgrid.alignCenter": "Align Center",
  "shared.microgrid.alignLeft": "Align Left",
  "shared.microgrid.alignMiddle": "Align Middle",
  "shared.microgrid.alignRight": "Align Right",
  "shared.microgrid.alignTop": "Align Top",
  "shared.microgrid.applyLabelOverride": "Apply label override",
  "shared.microgrid.applyTextChange": "Apply text change",
  "shared.microgrid.applyUrlChange": "Apply URL change",
  "shared.microgrid.backgroundColor": "Background Color",
  "shared.microgrid.backward": "Backward",
  "shared.microgrid.barBackgroundColor": "Bar Background Color",
  "shared.microgrid.barColor": "Bar Color",
  "shared.microgrid.bold": "Bold",
  "shared.microgrid.cable": "Cable",
  "shared.microgrid.cableActions": "Cable Actions",
  "shared.microgrid.cableStyle": "Cable Style",
  "shared.microgrid.color": "Color",
  "shared.microgrid.conditionN": "Condition {n}",
  "shared.microgrid.conditions": "Conditions",
  "shared.microgrid.conditionsAlwaysActive": "Conditions (Always Active)",
  "shared.microgrid.contain": "Contain",
  "shared.microgrid.copied": "Copied!",
  "shared.microgrid.copyLayoutJson": "Copy Layout JSON",
  "shared.microgrid.copyNode": "Copy Node",
  "shared.microgrid.copyWidget": "Copy Widget",
  "shared.microgrid.cover": "Cover",
  "shared.microgrid.dataPoint": "Data Point",
  "shared.microgrid.dataPoints": "Data Points",
  "shared.microgrid.debug.grid": "Grid:",
  "shared.microgrid.debug.mode": "Mode:",
  "shared.microgrid.debug.off": "OFF",
  "shared.microgrid.debug.on": "ON",
  "shared.microgrid.debug.snap": "Snap:",
  "shared.microgrid.debug.worldBounds": "World Bounds:",
  "shared.microgrid.debug.worldOrigin": "World (0, 0)",
  "shared.microgrid.debug.worldOriginViaWorldToScreen": "World (0, 0) via worldToScreen()",
  "shared.microgrid.decimalPrecision": "Decimal Precision",
  "shared.microgrid.defaultHighlightColor": "Default Highlight Color",
  "shared.microgrid.deleteAlert": "Delete Alert",
  "shared.microgrid.deleteCable": "Delete Cable",
  "shared.microgrid.deleteCondition": "Delete Condition",
  "shared.microgrid.deleteHighlight": "Delete Highlight",
  "shared.microgrid.deleteNode": "Delete Node",
  "shared.microgrid.deletePort": "Delete Port",
  "shared.microgrid.deleteWidget": "Delete Widget",
  "shared.microgrid.description": "Description",
  "shared.microgrid.disableSnapping": "Disable Snapping",
  "shared.microgrid.edit": "Edit",
  "shared.microgrid.editWidgets": "Edit Widgets",
  "shared.microgrid.enableSnapping": "Enable Snapping",
  "shared.microgrid.enterFullscreen": "Enter Fullscreen",
  "shared.microgrid.enterLabelText": "Enter label text",
  "shared.microgrid.errorPrefix": "Error: {message}",
  "shared.microgrid.errorSavingLayout": "Error saving layout",
  "shared.microgrid.exitEditMode": "Exit Edit Mode",
  "shared.microgrid.exitFullscreen": "Exit Fullscreen",
  "shared.microgrid.fill": "Fill",
  "shared.microgrid.fit": "Fit",
  "shared.microgrid.flowDirection": "Flow Direction",
  "shared.microgrid.forward": "Forward",
  "shared.microgrid.frameAll": "Frame All",
  "shared.microgrid.fullscreen": "Fullscreen",
  "shared.microgrid.gaugeBar": "Gauge Bar",
  "shared.microgrid.gaugeRing": "Gauge Ring",
  "shared.microgrid.hideLabel": "Hide Label",
  "shared.microgrid.hideUnit": "Hide Unit",
  "shared.microgrid.hideValue": "Hide Value",
  "shared.microgrid.highlight": "Highlight",
  "shared.microgrid.horizontalAlign": "Horizontal Align",
  "shared.microgrid.hoursAgo": "{count}h ago",
  "shared.microgrid.iconColor": "Icon Color",
  "shared.microgrid.iconLucide": "Icon (lucide.dev)",
  "shared.microgrid.iconMap": "Icon Map",
  "shared.microgrid.iconMappings": "Icon Mappings",
  "shared.microgrid.iconName": "Icon name",
  "shared.microgrid.iconSize": "Icon Size",
  "shared.microgrid.id": "ID",
  "shared.microgrid.idAlreadyExists": "ID already exists",
  "shared.microgrid.idCannotBeEmpty": "ID cannot be empty",
  "shared.microgrid.image": "Image",
  "shared.microgrid.imageCouldNotBeLoaded": "Image could not be loaded",
  "shared.microgrid.importExportLayout": "Import / Export Layout",
  "shared.microgrid.invalidJson": "Invalid JSON",
  "shared.microgrid.invalidUrl": "Invalid URL",
  "shared.microgrid.justNow": "just now",
  "shared.microgrid.label": "Label",
  "shared.microgrid.labelColor": "Label Color",
  "shared.microgrid.labelOverride": "Label Override",
  "shared.microgrid.labelSize": "Label Size",
  "shared.microgrid.labelWeight": "Label Weight",
  "shared.microgrid.layoutReset": "Layout Reset!",
  "shared.microgrid.leaveEmptyToUseDefault": "Leave empty to use default",
  "shared.microgrid.loadLayout": "Load Layout",
  "shared.microgrid.lowerThreshold": "Lower Threshold",
  "shared.microgrid.margin": "Margin",
  "shared.microgrid.maxValue": "Max Value",
  "shared.microgrid.minutesAgo": "{count}m ago",
  "shared.microgrid.minValue": "Min Value",
  "shared.microgrid.mockData": "MOCK DATA",
  "shared.microgrid.mockDataTooltip": "Mock data mode is enabled. Live data is simulated with realistic values. Add ?mock=false to use real TimeStream data.",
  "shared.microgrid.name": "Name",
  "shared.microgrid.newAlert": "New Alert",
  "shared.microgrid.noAlerts": "No alerts. Add an alert to activate this highlight.",
  "shared.microgrid.noData": "No Data",
  "shared.microgrid.noDataPointsAvailable": "No data points available",
  "shared.microgrid.node": "Node",
  "shared.microgrid.nodeColor": "Node Color",
  "shared.microgrid.noFlow": "No Flow",
  "shared.microgrid.noMatchingDataPoints": "No matching data points",
  "shared.microgrid.none": "None",
  "shared.microgrid.noneManualControl": "None (Manual Control)",
  "shared.microgrid.normal": "Normal",
  "shared.microgrid.operator": "Operator",
  "shared.microgrid.padding": "Padding",
  "shared.microgrid.pasteLayoutJson": "Paste Layout JSON",
  "shared.microgrid.port": "Port",
  "shared.microgrid.readout": "Readout",
  "shared.microgrid.removeMapping": "Remove mapping",
  "shared.microgrid.resetLayout": "Reset Layout",
  "shared.microgrid.ringBackgroundColor": "Ring Background Color",
  "shared.microgrid.ringColor": "Ring Color",
  "shared.microgrid.ringSize": "Ring Size",
  "shared.microgrid.saved": "Saved",
  "shared.microgrid.savedAgo": "Saved {time}",
  "shared.microgrid.saving": "Saving...",
  "shared.microgrid.scaleDown": "Scale Down",
  "shared.microgrid.searchDataPoints": "Search data points...",
  "shared.microgrid.secondsAgo": "{count}s ago",
  "shared.microgrid.selectDataPoint": "-- Select Data Point --",
  "shared.microgrid.selectDataPointBind": "Select a data point to bind cable animation",
  "shared.microgrid.settings": "Settings",
  "shared.microgrid.size": "Size",
  "shared.microgrid.smooth": "Smooth",
  "shared.microgrid.speedFromLiveData": "Speed and offline state derived from live data",
  "shared.microgrid.stepped": "Stepped",
  "shared.microgrid.straight": "Straight",
  "shared.microgrid.style": "Style",
  "shared.microgrid.stylingProperties": "Styling Properties",
  "shared.microgrid.targetId": "Target ID",
  "shared.microgrid.targetType": "Target Type",
  "shared.microgrid.text": "Text",
  "shared.microgrid.title": "Title",
  "shared.microgrid.toggleFlowDirection": "Toggle Flow Direction",
  "shared.microgrid.unitColor": "Unit Color",
  "shared.microgrid.unitSize": "Unit Size",
  "shared.microgrid.unitWeight": "Unit Weight",
  "shared.microgrid.unknownStatus": "Unknown status",
  "shared.microgrid.unknownWidgetType": "Unknown widget type",
  "shared.microgrid.untitled": "Untitled",
  "shared.microgrid.upperThreshold": "Upper Threshold",
  "shared.microgrid.url": "URL",
  "shared.microgrid.urlCannotBeEmpty": "URL cannot be empty",
  "shared.microgrid.value": "Value",
  "shared.microgrid.valueColor": "Value Color",
  "shared.microgrid.valueSize": "Value Size",
  "shared.microgrid.valueWeight": "Value Weight",
  "shared.microgrid.variant": "Variant",
  "shared.microgrid.verticalAlign": "Vertical Align",
  "shared.microgrid.widget": "Widget",
  "shared.microgrid.zoomIn": "Zoom In",
  "shared.microgrid.zoomOut": "Zoom Out",
  "shared.nav.closeNavigationMenu": "Close navigation menu",
  "shared.nav.collapse": "Collapse",
  "shared.nav.collapseSidebar": "Collapse sidebar",
  "shared.nav.expand": "Expand",
  "shared.nav.expandSidebar": "Expand sidebar",
  "shared.nav.joinOrganization": "Join an organization",
  "shared.nav.mainNavigation": "Main navigation",
  "shared.nav.openNavigationMenu": "Open navigation menu",
  "shared.nav.openProfileMenu": "Open profile menu",
  "shared.nav.productSwitcher": "Product switcher",
  "shared.nav.toggleNavigation": "Toggle navigation",
  "shared.table.clearSelection": "Clear selection",
  "shared.table.columns": "Columns",
  "shared.table.customRange": "Custom range",
  "shared.table.dateRangePreset": "Date range preset",
  "shared.table.filters": "Filters",
  "shared.table.loadingResults": "Loading results",
  "shared.table.moreOptions": "More options",
  "shared.table.next": "Next",
  "shared.table.nextCta": "Next CTA",
  "shared.table.nextMonth": "Next month",
  "shared.table.previous": "Previous",
  "shared.table.previousMonth": "Previous month",
  "shared.table.range": "Range",
  "shared.table.rowsPerPage": "Rows per page",
  "shared.table.selectStartOrEndDate": "Select start or end date",
  "shared.ui.closeModal": "Close modal",
  "shared.ui.dismissToast": "Dismiss toast",
  "shared.ui.moreActions": "More actions",
  "shared.ui.scrollToTop": "Scroll to top",
  "shared.ui.top": "Top",
  "shared.userMenu.emailNotVerified": "Email not verified. Click to verify.",
};
